import React, { Suspense, useRef, useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { Canvas, useFrame, useThree, useLoader, extend } from '@react-three/fiber';
import { 
  OrbitControls, 
  Box, 
  RoundedBox,
  Cylinder, 
  Plane, 
  Sphere,
  Torus,
  useTexture,
  Text,
  Html,
  Environment,
  ContactShadows,
  PerspectiveCamera,
  AdaptiveDpr,
  AdaptiveEvents,
  shaderMaterial,
  useGLTF,
  Capsule,
  Icosahedron
} from '@react-three/drei';
import * as THREE from 'three';
import { RepeatWrapping } from 'three'
import { RGBELoader } from 'three-stdlib'; // or EXRLoader if you're using .exr

import { Shape, ExtrudeGeometry, MeshStandardMaterial, Color, RectAreaLight } from 'three';
import { mockPaintings, gallerySettings, paintingImage } from '../data/mockGalleryData';
import { DayNightDriver, PictureLights, ShaderPrewarm, ReadySignal, TIMES, createSkyState, duskColor } from './scene/DayNight';
import { Moon, TwinklingStars, ShootingStars, Aurora } from './scene/NightSky';
import { WeatherDriver, Rain, Snow, Leaves, RoofRain, createWeatherState } from './scene/Weather';
import { TheatreDriver, TheatreSet, ScreenVideo, THEATRE } from './scene/Theatre';
import { CloudSky } from './scene/CloudSky';
import GlassDoor, { DOOR } from './scene/GlassDoor';
import WallClock from './scene/WallClock';
import { WallWithWindows, HALL_WINDOWS } from './scene/HallWindows';
import Landscape from './scene/Landscape';
import Wildlife from './scene/Wildlife';
import Puddles from './scene/Puddles';
import EasterEggs from './scene/EasterEggs';
import Festivals from './scene/Festivals';
import { festivalToday } from '../lib/festivals';
import { eggState, announceEgg, playNeigh, playChime } from '../lib/eggs';
import { HelpCircle } from 'lucide-react';

import YouTubeFrame from './YouTubeFrame';
import { isTypingTarget } from '../lib/typing';
import { SimplexNoise } from 'three/examples/jsm/math/SimplexNoise.js';


// Gallery Environment Components
// Floor component with click navigation
const GalleryFloor = ({ onFloorClick, isGuideMode }) => {
  const floorTex = gallerySettings.floor;
  const { gl } = useThree(); // Get canvas element

  const [
    colorMap,
    roughnessMap,
    normalMap,
  ] = useTexture([
    floorTex.colorMap,
    floorTex.roughnessMap,
    floorTex.normalMap,
  ]);

  [colorMap, roughnessMap, normalMap].forEach(tex => {
    tex.wrapS = tex.wrapT = RepeatWrapping;
    tex.repeat.set(...floorTex.repeat);
  });

  const { walls } = gallerySettings;

  const handleFloorClick = (e) => {
    if (!isGuideMode) {
      e.stopPropagation();
      const point = e.point;
      onFloorClick?.(point);
    }
  };

  // --- CORRECTED: SVG Animated Flat (Larger, Horizontal Rotation) Metallic Gold TARGET Cursor ---
  const createAnimatedTargetCursorSVG = () => {
    // Ellipse parameters
    const rx = 20;
    const ry = 5;
    const strokeColor = "#FFD700";
    const strokeWidth = 3;

    // Approximate circumference
    const h = Math.pow(rx - ry, 2) / Math.pow(rx + ry, 2);
    const circumference = Math.PI * (rx + ry) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));

    // Dash pattern: 4 dashes, 4 gaps
    const dashLength = circumference / 8;
    const gapLength = circumference / 8;

    // SVG dimensions and center
    const svgWidth = 44;
    const svgHeight = 22;
    const centerX = svgWidth / 2;
    const centerY = svgHeight / 2;

    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" 
           width="${svgWidth}" height="${svgHeight}" 
           viewBox="0 0 ${svgWidth} ${svgHeight}">
        
        <ellipse 
          cx="${centerX}" cy="${centerY}" 
          rx="${rx}" ry="${ry}" 
          fill="none" 
          stroke="${strokeColor}" 
          stroke-width="${strokeWidth}" 
          opacity="0.85"
          stroke-dasharray="${dashLength} ${gapLength}"
        >
          {/* Animation MUST be a child of the element it animates */}
          <animate 
             attributeName="stroke-dashoffset" 
             attributeType="XML"
             from="0" 
             to="${circumference}" 
             dur="2s"             
             repeatCount="indefinite" />
        </ellipse>

        {/* Optional: Static Center Dot */}
         <circle 
           cx="${centerX}" cy="${centerY}" 
           r="1.5" 
           fill="${strokeColor}" 
           opacity="0.9"
         />

      </svg>
    `;
    // Encode for use in data URI
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  };

  // Generate the animated cursor URL once using useMemo
  const animatedTargetCursorUrl = useMemo(createAnimatedTargetCursorSVG, []);
  // --- END CORRECTED ---

  const handlePointerOver = (e) => {
    e.stopPropagation();
    // Use the animated target SVG, adjust hotspot (44x22 -> 22,11)
    gl.domElement.style.cursor = `url('${animatedTargetCursorUrl}') 22 11, auto`;
  };

  const handlePointerOut = (e) => {
    gl.domElement.style.cursor = 'grab';
  };

  return (
    <Plane
      args={[walls.width, walls.depth, 256, 256]}
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, 0, 0]}
      receiveShadow
      onClick={handleFloorClick}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <meshStandardMaterial
        map={colorMap}
        roughnessMap={roughnessMap}
        normalMap={normalMap}
        roughness={floorTex.roughness}
        metalness={floorTex.metalness}
      />
    </Plane>
  );
};

// const GalleryFloor = ({ onFloorClick, isGuideMode }) => {
//   const floorTex = gallerySettings.floor;

//   const [colorMap, roughnessMap, normalMap] = useTexture([
//     floorTex.colorMap,
//     floorTex.roughnessMap,
//     floorTex.normalMap
//   ]);
  
//   [colorMap, roughnessMap, normalMap].forEach(tex => {
//     tex.wrapS = tex.wrapT = RepeatWrapping;
//     tex.repeat.set(...floorTex.repeat);
//   });

//   const { walls } = gallerySettings;
  
//   const handleFloorClick = (e) => {
//     if (!isGuideMode) {
//       e.stopPropagation();
//       const point = e.point;
//       onFloorClick?.(point);
//     }
//   };
  
//   return (
//     <Plane 
//       args={[walls.width, walls.depth]} 
//       rotation={[-Math.PI / 2, 0, 0]} 
//       position={[0, 0, 0]}
//       receiveShadow
//       onClick={handleFloorClick}
//     >
//       <meshStandardMaterial 
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={floorTex.roughness}
//         metalness={floorTex.metalness}

//         // color={gallerySettings.floor.color} 
//         // roughness={0.1}
//         // metalness={0.05}
//       />
//     </Plane>
//   );
// };

// const Cornice = ({ position, rotation = [0, 0, 0], scale = [0.05, 0.05, 0.05] }) => {
//   const { scene } = useGLTF('/models/cornice.glb');

//   return (
//     <primitive
//       object={scene.clone()} // avoid sharing same instance
//       position={position}
//       rotation={rotation}
//       scale={scale}
//       castShadow
//       receiveShadow
//     />
//   );
// };

// const PlaceholderCornice = ({ position, scale }) => (
//   <Box args={scale} position={position}>
//     <meshStandardMaterial color="#ffffff" metalness={0.8} roughness={0.2} emissive="#FFD700" emissiveIntensity={0.2} />
//   </Box>
// );

const PlaceholderCornice = ({ position, rotation = [0, 0, 0], length, isHorizontal }) => {
  const corniceSize = isHorizontal ? [length, 0.1, 0.1] : [0.1, 0.1, length];

  return (
    <Box args={corniceSize} position={position} rotation={rotation}>
      <meshStandardMaterial color="#ffffff" roughness={0.85} metalness={0.05} />
    </Box>
  );
};

const LightStrip = ({ position, rotation = [0, 0, 0], length, isHorizontal }) => {
  const lightSize = isHorizontal ? [length, 0.02, 0.02] : [0.02, 0.02, length];

  return (
    <Box args={lightSize} position={position} rotation={rotation}>
      <meshStandardMaterial 
        color="#ffffff"
        emissive="#ffffff" 
        emissiveIntensity={2.5}
        roughness={0.2}
        metalness={0.05}
      />
    </Box>
  );
};
export const VisibleSkySphere = ({ hdriPath = '/textures/sunny_day_2k.hdr' }) => {
  const texture = useLoader(RGBELoader, hdriPath);
  texture.mapping = THREE.EquirectangularReflectionMapping;

  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[100, 64, 64]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} />
    </mesh>
  );
};
export const RotatingHDRISkyDome = ({ hdriPath = '/textures/sunny_day_2k.hdr', nightRef, skyRef, weatherRef, theatreRef }) => {
  const { scene } = useThree();
  const texture = useLoader(RGBELoader, hdriPath);

  texture.mapping = THREE.EquirectangularReflectionMapping;

  // Set environment for reflections
  scene.environment = texture;
  scene.background = texture;

  // The visible sky (photo sky on clear days, climate-matched clouds otherwise).
  return <CloudSky texture={texture} nightRef={nightRef} skyRef={skyRef} weatherRef={weatherRef} theatreRef={theatreRef} />;
};

// const RotatingHDRISkyDome = ({ hdriPath = '/textures/sunny_day_2k.hdr', rotationSpeed = 0.0005 }) => {
//   const { scene } = useThree();
//   const skyRef = useRef();

//   // Load HDRI
//   const texture = useLoader(RGBELoader, hdriPath);
//   texture.mapping = THREE.EquirectangularReflectionMapping;

//   // Set environment and background once loaded
//   useEffect(() => {
//     scene.environment = texture;
//     scene.background = texture;
//   }, [texture, scene]);

//   // Rotate the sphere slowly
//   useFrame(() => {
//     if (skyRef.current) {
//       skyRef.current.rotation.y += rotationSpeed;
//     }
//   });

//   return (
//     <mesh ref={skyRef}>
//       <sphereGeometry args={[100, 64, 64]} />
//       <meshBasicMaterial
//         envMap={texture}
//         side={THREE.BackSide}
//       />
//     </mesh>
//   );
// };


// --- Glass Panel Component ---
// const GlassPanel = ({ points, materialProps, ...props }) => {
//   const shape = useMemo(() => {
//     const s = new THREE.Shape();
//     s.moveTo(points[0].x, points[0].y);
//     for (let i = 1; i < points.length; i++) {
//       s.lineTo(points[i].x, points[i].y);
//     }
//     s.closePath();
//     return s;
//   }, [points]);

//   return (
//     <mesh {...props}>
//       <shapeGeometry args={[shape]} />
//       <meshPhysicalMaterial {...materialProps} />
//     </mesh>
//   );
// };


// const GlassPanel = ({ size = [4, 4], position = [0, 0, 0], rotation = [0, 0, 0] }) => {
//   const glassMaterial = useMemo(() => new THREE.MeshPhysicalMaterial({
//     color: '#cfe4f7',
//     roughness: 0.05,
//     metalness: 0,
//     transmission: 1,             // Realistic glass-like transparency
//     thickness: 0.3,              // Visible thickness
//     reflectivity: 0.4,           // Some light reflections
//     clearcoat: 1,                // Smooth glossy surface
//     clearcoatRoughness: 0.1,
//     ior: 1.45,                   // Index of refraction for glass
//     side: THREE.DoubleSide,
//     transparent: true
//   }), []);

//   return (
//     <Plane args={size} position={position} rotation={rotation}>
//       <primitive object={glassMaterial} attach="material" />
//     </Plane>
//   );
// };


// --- Glass Panel Component (NEW ROBUST VERSION) ---
// This component now builds the geometry directly in 3D space from its corner points.
const GlassPanel = ({ corners, materialProps, ...props }) => {
  const geometry = useMemo(() => {
    const geom = new THREE.BufferGeometry();
    // Create vertices from the 3D corner points
    const vertices = new Float32Array([
      corners[0].x, corners[0].y, corners[0].z, // Vertex 0
      corners[1].x, corners[1].y, corners[1].z, // Vertex 1
      corners[2].x, corners[2].y, corners[2].z, // Vertex 2
      corners[3].x, corners[3].y, corners[3].z, // Vertex 3
    ]);
    // Create two triangular faces (0,1,2 and 0,2,3)
    const indices = new Uint16Array([0, 1, 2, 0, 2, 3]);

    geom.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geom.setIndex(new THREE.BufferAttribute(indices, 1));
    geom.computeVertexNormals();
    return geom;
  }, [corners]);

  return (
    <mesh geometry={geometry} {...props}>
      <meshPhysicalMaterial {...materialProps} />
    </mesh>
  );
};

// --- Support Beam Component ---
const SupportBeam = (props) => (
  <Box {...props}>
    <meshStandardMaterial color="#2c3e50" roughness={0.7} metalness={0.2} />
  </Box>
);

// --- Angled Beam Component ---
const AngledBeam = ({ start, end, thickness }) => {
  const length = start.distanceTo(end);
  const group = useMemo(() => {
    const obj = new THREE.Object3D();
    obj.position.copy(start);
    obj.lookAt(end);
    return obj;
  }, [start, end]);

  return (
    <group position={group.position} quaternion={group.quaternion}>
      <SupportBeam args={[thickness, thickness, length]} position={[0, 0, length / 2]} />
    </group>
  );
};

// --- Roof Lantern Component ---
const RoofLantern = ({ walls, weatherRef }) => {
  const lanternHeight = 2.0;
  const lanternInset = 3.5;
  const beamThickness = 0.35;

  // --- Realistic Glass Materials ---
  const translucentMaterial = useMemo(() => ({
    thickness: 1.5,
    roughness: 0.3, // was 0.6 – clearer so the weather outside is visible
    transmission: 0.9,
    ior: 1,
    envMapIntensity: 10,
    attenuationColor: "#87ceeb",
    attenuationDistance: 2.0, // was 0.5 – lighter blue tint so the sky/clouds show their real colour
    side: THREE.DoubleSide,
  }), []);

  const transparentMaterial = useMemo(() => ({
    thickness: 1.0,
    roughness: 0.1, // was 0.3
    transmission: 0.95,
    ior: 1,
    envMapIntensity: 20,
    attenuationColor: "#add8e6",
    attenuationDistance: 2.5, // was 0.7
    side: THREE.DoubleSide,
  }), []);

  // Define the 8 key points of the roof structure
  const points = useMemo(() => {
    const halfW = walls.width / 2;
    const halfD = walls.depth / 2;
    const topY = walls.height + lanternHeight;
    const topHalfW = halfW - lanternInset;
    const topHalfD = halfD - lanternInset;
    
    // --- THE FIX IS HERE ---
    // We slightly enlarge the glass panels so they intersect the beams.
    const glassOverlap = 0.1; 

    const basePoints = {
      fr: new THREE.Vector3(halfW + glassOverlap, walls.height, halfD + glassOverlap),
      fl: new THREE.Vector3(-halfW - glassOverlap, walls.height, halfD + glassOverlap),
      br: new THREE.Vector3(halfW + glassOverlap, walls.height, -halfD - glassOverlap),
      bl: new THREE.Vector3(-halfW - glassOverlap, walls.height, -halfD - glassOverlap),
    };

    const topPoints = {
      fr: new THREE.Vector3(topHalfW + glassOverlap, topY, topHalfD + glassOverlap),
      fl: new THREE.Vector3(-topHalfW - glassOverlap, topY, topHalfD + glassOverlap),
      br: new THREE.Vector3(topHalfW + glassOverlap, topY, -topHalfD - glassOverlap),
      bl: new THREE.Vector3(-topHalfW - glassOverlap, topY, -topHalfD - glassOverlap),
    };

    return {
      base_fr: basePoints.fr, base_fl: basePoints.fl, base_br: basePoints.br, base_bl: basePoints.bl,
      top_fr: topPoints.fr, top_fl: topPoints.fl, top_br: topPoints.br, top_bl: topPoints.bl,
    };
  }, [walls, lanternHeight, lanternInset]);

  // --- Additional Support Beams ---
  const additionalBeams = useMemo(() => {
    const beams = [];
    const numIntermediateRafters = 3; 
    const smallBeamThickness = beamThickness * 0.7;

    // Top cross beams
    beams.push(<AngledBeam key="top_cross_1" start={points.top_fl} end={points.top_br} thickness={smallBeamThickness} />);
    beams.push(<AngledBeam key="top_cross_2" start={points.top_fr} end={points.top_bl} thickness={smallBeamThickness} />);
    
    // Central beam from North to South
    const centerTopFront = new THREE.Vector3().lerpVectors(points.top_fl, points.top_fr, 0.5);
    const centerTopBack = new THREE.Vector3().lerpVectors(points.top_bl, points.top_br, 0.5);
    beams.push(<AngledBeam key="top_center_spine_ns" start={centerTopFront} end={centerTopBack} thickness={beamThickness} />);

    // Central beam from East to West
    const centerTopLeft = new THREE.Vector3().lerpVectors(points.top_bl, points.top_fl, 0.5);
    const centerTopRight = new THREE.Vector3().lerpVectors(points.top_br, points.top_fr, 0.5);
    beams.push(<AngledBeam key="top_center_spine_ew" start={centerTopLeft} end={centerTopRight} thickness={beamThickness} />);


    // Intermediate rafters (Jack Rafters)
    for (let i = 1; i <= numIntermediateRafters; i++) {
      const t = i / (numIntermediateRafters + 1);
      
      beams.push(<AngledBeam key={`jack_f_${i}`} start={new THREE.Vector3().lerpVectors(points.base_fl, points.base_fr, t)} end={new THREE.Vector3().lerpVectors(points.top_fl, points.top_fr, t)} thickness={smallBeamThickness} />);
      beams.push(<AngledBeam key={`jack_b_${i}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.base_br, t)} end={new THREE.Vector3().lerpVectors(points.top_bl, points.top_br, t)} thickness={smallBeamThickness} />);
      beams.push(<AngledBeam key={`jack_l_${i}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.base_fl, t)} end={new THREE.Vector3().lerpVectors(points.top_bl, points.top_fl, t)} thickness={smallBeamThickness} />);
      beams.push(<AngledBeam key={`jack_r_${i}`} start={new THREE.Vector3().lerpVectors(points.base_br, points.base_fr, t)} end={new THREE.Vector3().lerpVectors(points.top_br, points.top_fr, t)} thickness={smallBeamThickness} />);
    }

    // Purlins (Horizontal supports on the slope)
    const purlinT1 = 0.33;
    const purlinT2 = 0.66;
    const purlinThickness = beamThickness * 0.9;

    [purlinT1, purlinT2].forEach(t => {
        beams.push(<AngledBeam key={`purlin_f_${t}`} start={new THREE.Vector3().lerpVectors(points.base_fl, points.top_fl, t)} end={new THREE.Vector3().lerpVectors(points.base_fr, points.top_fr, t)} thickness={purlinThickness} />);
        beams.push(<AngledBeam key={`purlin_b_${t}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.top_bl, t)} end={new THREE.Vector3().lerpVectors(points.base_br, points.top_br, t)} thickness={purlinThickness} />);
        beams.push(<AngledBeam key={`purlin_l_${t}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.top_bl, t)} end={new THREE.Vector3().lerpVectors(points.base_fl, points.top_fl, t)} thickness={purlinThickness} />);
        beams.push(<AngledBeam key={`purlin_r_${t}`} start={new THREE.Vector3().lerpVectors(points.base_br, points.top_br, t)} end={new THREE.Vector3().lerpVectors(points.base_fr, points.top_fr, t)} thickness={purlinThickness} />);
    });

    return beams;
  }, [points, beamThickness]);

  return (
    <group>
      {/* --- BEAM STRUCTURE --- */}
      <AngledBeam start={points.base_fl} end={points.base_fr} thickness={beamThickness} />
      <AngledBeam start={points.base_bl} end={points.base_br} thickness={beamThickness} />
      <AngledBeam start={points.base_fl} end={points.base_bl} thickness={beamThickness} />
      <AngledBeam start={points.base_fr} end={points.base_br} thickness={beamThickness} />
      <AngledBeam start={points.top_fl} end={points.top_fr} thickness={beamThickness} />
      <AngledBeam start={points.top_bl} end={points.top_br} thickness={beamThickness} />
      <AngledBeam start={points.top_fl} end={points.top_bl} thickness={beamThickness} />
      <AngledBeam start={points.top_fr} end={points.top_br} thickness={beamThickness} />
      <AngledBeam start={points.base_fr} end={points.top_fr} thickness={beamThickness} />
      <AngledBeam start={points.base_fl} end={points.top_fl} thickness={beamThickness} />
      <AngledBeam start={points.base_br} end={points.top_br} thickness={beamThickness} />
      <AngledBeam start={points.base_bl} end={points.top_bl} thickness={beamThickness} />
      {additionalBeams}

      {/* --- GLASS PANELS (using the new robust component) --- */}
      <GlassPanel materialProps={translucentMaterial} corners={[points.base_fl, points.base_fr, points.top_fr, points.top_fl]} />
      <GlassPanel materialProps={translucentMaterial} corners={[points.base_br, points.base_bl, points.top_bl, points.top_br]} />
      <GlassPanel materialProps={translucentMaterial} corners={[points.base_fr, points.base_br, points.top_br, points.top_fr]} />
      <GlassPanel materialProps={translucentMaterial} corners={[points.base_bl, points.base_fl, points.top_fl, points.top_bl]} />
      <GlassPanel materialProps={transparentMaterial} corners={[points.top_fl, points.top_fr, points.top_br, points.top_bl]} />

      {/* --- Raindrops splattering and rolling down the inside of the glass --- */}
      {weatherRef && (
        <RoofRain
          weatherRef={weatherRef}
          panels={[
            { corners: [points.base_fl, points.base_fr, points.top_fr, points.top_fl], sloped: true },
            { corners: [points.base_br, points.base_bl, points.top_bl, points.top_br], sloped: true },
            { corners: [points.base_fr, points.base_br, points.top_br, points.top_fr], sloped: true },
            { corners: [points.base_bl, points.base_fl, points.top_fl, points.top_bl], sloped: true },
            { corners: [points.top_fl, points.top_fr, points.top_br, points.top_bl], sloped: false },
          ]}
        />
      )}
    </group>
  );
};

const GalleryWalls = ({ weatherRef, windowHandlers }) => {
  const { walls } = gallerySettings;

  const [colorMap, roughnessMap, normalMap] = useTexture([
    walls.colorMap,
    walls.roughnessMap,
    walls.normalMap,
  ]);

  useMemo(() => {
    [colorMap, roughnessMap, normalMap].forEach(tex => {
      tex.wrapS = tex.wrapT = RepeatWrapping;
      tex.repeat.set(...walls.repeat);
    });
  }, [colorMap, roughnessMap, normalMap, walls.repeat]);

  const materialProps = {
    map: colorMap,
    roughnessMap: roughnessMap,
    normalMap: normalMap,
    roughness: walls.roughness,
  };
  const wallMaterial = useMemo(() => new THREE.MeshStandardMaterial(materialProps), [colorMap, roughnessMap, normalMap]); // eslint-disable-line

  return (
    <group>
      {/* === Walls (All 4 are now solid boxes) === */}
      {/* North, east and west walls with glass windows looking out over the landscape */}
      <WallWithWindows length={walls.width} height={walls.height} windows={HALL_WINDOWS.north} material={wallMaterial} {...windowHandlers} position={[0, 0, -walls.depth / 2]} />
      <WallWithWindows length={walls.depth} height={walls.height} windows={HALL_WINDOWS.east} material={wallMaterial} {...windowHandlers} position={[walls.width / 2, 0, 0]} rotation={[0, -Math.PI / 2, 0]} />
      <WallWithWindows length={walls.depth} height={walls.height} windows={HALL_WINDOWS.west} material={wallMaterial} {...windowHandlers} position={[-walls.width / 2, 0, 0]} rotation={[0, Math.PI / 2, 0]} />
      {/* South wall, built around the opening for the glass door to the Grand Salon */}
      {[-1, 1].map((s) => {
        const segW = walls.width / 2 - DOOR.width / 2 - 0.12;
        return (
          <Box key={s} args={[segW, walls.height, 0.5]} position={[s * (walls.width / 2 - segW / 2), walls.height / 2, walls.depth / 2]}>
            <meshStandardMaterial {...materialProps} />
          </Box>
        );
      })}
      <Box args={[DOOR.width + 0.24, walls.height - DOOR.height - 0.12, 0.5]} position={[0, (walls.height + DOOR.height + 0.12) / 2, walls.depth / 2]}>
        <meshStandardMaterial {...materialProps} />
      </Box>

      {/* === New Roof Lantern Structure === */}
      <RoofLantern walls={walls} weatherRef={weatherRef} />
    </group>
  );
};
// const GalleryWalls = () => {
  
//   const { walls } = gallerySettings;

//   const [colorMap, roughnessMap, normalMap] = useTexture([
//     walls.colorMap,
//     walls.roughnessMap,
//     walls.normalMap,
//   ]);

//   const wallMaterial = useMemo(() => {
//     [colorMap, roughnessMap, normalMap].forEach(tex => {
//       tex.wrapS = tex.wrapT = RepeatWrapping;
//       tex.repeat.set(...walls.repeat);
//     });
//     return (
//       <meshStandardMaterial
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//     );
//   }, [colorMap, roughnessMap, normalMap, walls]);

//   return (
//     <group>
//       {/* === Walls (All 4 are now solid boxes) === */}
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, -walls.depth / 2]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, walls.depth / 2]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[walls.width / 2, walls.height / 2, 0]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[-walls.width / 2, walls.height / 2, 0]}>
//         {wallMaterial}
//       </Box>
      
//       {/* === New Roof Lantern Structure === */}
//       <RoofLantern walls={walls} />
//     </group>
//   );
// };


// // --- Glass Panel Component ---
// const GlassPanel = ({ points, materialProps, ...props }) => {
//   const shape = useMemo(() => {
//     const s = new THREE.Shape();
//     s.moveTo(points[0].x, points[0].y);
//     for (let i = 1; i < points.length; i++) {
//       s.lineTo(points[i].x, points[i].y);
//     }
//     s.closePath();
//     return s;
//   }, [points]);

//   return (
//     <mesh {...props}>
//       <shapeGeometry args={[shape]} />
//       <meshPhysicalMaterial {...materialProps} />
//     </mesh>
//   );
// };

// // --- Support Beam Component ---
// const SupportBeam = (props) => (
//   <Box {...props}>
//     <meshStandardMaterial color="#2c3e50" roughness={0.7} metalness={0.2} />
//   </Box>
// );

// // --- Angled Beam Component ---
// const AngledBeam = ({ start, end, thickness }) => {
//   const length = start.distanceTo(end);
//   const group = useMemo(() => {
//     const obj = new THREE.Object3D();
//     obj.position.copy(start);
//     obj.lookAt(end);
//     return obj;
//   }, [start, end]);

//   return (
//     <group position={group.position} quaternion={group.quaternion}>
//       <SupportBeam args={[thickness, thickness, length]} position={[0, 0, length / 2]} />
//     </group>
//   );
// };

// // --- Roof Lantern Component ---
// const RoofLantern = ({ walls }) => {
//   const lanternHeight = 2.0;
//   const lanternInset = 3.5;
//   const beamThickness = 0.35;

//   // --- THE FIX IS HERE ---
//   // We are now using your texture as an alphaMap to directly control transparency.
//   const glassAlphaMap = useTexture('/textures/glass_normal.png');
//   glassAlphaMap.wrapS = glassAlphaMap.wrapT = RepeatWrapping;
//   glassAlphaMap.repeat.set(1, 1);


//   // Material properties for different glass types, now with an alpha map
//   const translucentMaterial = useMemo(() => ({
//     thickness: 1.0,
//     transmission: 0.9,
//     ior: 1.5,
//     envMapIntensity: 10,
//     attenuationColor: "#87ceeb",
//     attenuationDistance: 0.5,
//     roughness: 0.2,
//     transparent: true, // Required for alphaMap to work
//     alphaMap: glassAlphaMap, // Apply the loaded texture as an alpha map
//     side: THREE.DoubleSide,
//   }), [glassAlphaMap]);

//   const transparentMaterial = useMemo(() => ({
//     thickness: 0.5,
//     transmission: 0.95,
//     ior: 1.5,
//     envMapIntensity: 20,
//     attenuationColor: "#add8e6",
//     attenuationDistance: 0.7,
//     roughness: 0.1,
//     transparent: true, // Required for alphaMap to work
//     alphaMap: glassAlphaMap, // Apply the loaded texture as an alpha map
//     side: THREE.DoubleSide,
//   }), [glassAlphaMap]);

//   // Define the 8 key points of the roof structure
//   const points = useMemo(() => {
//     const halfW = walls.width / 2;
//     const halfD = walls.depth / 2;
//     const topY = walls.height + lanternHeight;
//     const topHalfW = halfW - lanternInset;
//     const topHalfD = halfD - lanternInset;

//     return {
//       base_fr: new THREE.Vector3(halfW, walls.height, halfD),
//       base_fl: new THREE.Vector3(-halfW, walls.height, halfD),
//       base_br: new THREE.Vector3(halfW, walls.height, -halfD),
//       base_bl: new THREE.Vector3(-halfW, walls.height, -halfD),
//       top_fr: new THREE.Vector3(topHalfW, topY, topHalfD),
//       top_fl: new THREE.Vector3(-topHalfW, topY, topHalfD),
//       top_br: new THREE.Vector3(topHalfW, topY, -topHalfD),
//       top_bl: new THREE.Vector3(-topHalfW, topY, -topHalfD),
//     };
//   }, [walls, lanternHeight, lanternInset]);

//   // --- Additional Support Beams ---
//   const additionalBeams = useMemo(() => {
//     const beams = [];
//     const numIntermediateRafters = 3; 
//     const smallBeamThickness = beamThickness * 0.7;

//     // Top cross beams
//     beams.push(<AngledBeam key="top_cross_1" start={points.top_fl} end={points.top_br} thickness={smallBeamThickness} />);
//     beams.push(<AngledBeam key="top_cross_2" start={points.top_fr} end={points.top_bl} thickness={smallBeamThickness} />);
    
//     // Central beam from North to South
//     const centerTopFront = new THREE.Vector3().lerpVectors(points.top_fl, points.top_fr, 0.5);
//     const centerTopBack = new THREE.Vector3().lerpVectors(points.top_bl, points.top_br, 0.5);
//     beams.push(<AngledBeam key="top_center_spine_ns" start={centerTopFront} end={centerTopBack} thickness={beamThickness} />);

//     // Central beam from East to West
//     const centerTopLeft = new THREE.Vector3().lerpVectors(points.top_bl, points.top_fl, 0.5);
//     const centerTopRight = new THREE.Vector3().lerpVectors(points.top_br, points.top_fr, 0.5);
//     beams.push(<AngledBeam key="top_center_spine_ew" start={centerTopLeft} end={centerTopRight} thickness={beamThickness} />);


//     // Intermediate rafters (Jack Rafters)
//     for (let i = 1; i <= numIntermediateRafters; i++) {
//       const t = i / (numIntermediateRafters + 1);
      
//       beams.push(<AngledBeam key={`jack_f_${i}`} start={new THREE.Vector3().lerpVectors(points.base_fl, points.base_fr, t)} end={new THREE.Vector3().lerpVectors(points.top_fl, points.top_fr, t)} thickness={smallBeamThickness} />);
//       beams.push(<AngledBeam key={`jack_b_${i}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.base_br, t)} end={new THREE.Vector3().lerpVectors(points.top_bl, points.top_br, t)} thickness={smallBeamThickness} />);
//       beams.push(<AngledBeam key={`jack_l_${i}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.base_fl, t)} end={new THREE.Vector3().lerpVectors(points.top_bl, points.top_fl, t)} thickness={smallBeamThickness} />);
//       beams.push(<AngledBeam key={`jack_r_${i}`} start={new THREE.Vector3().lerpVectors(points.base_br, points.base_fr, t)} end={new THREE.Vector3().lerpVectors(points.top_br, points.top_fr, t)} thickness={smallBeamThickness} />);
//     }

//     // Purlins (Horizontal supports on the slope)
//     const purlinT1 = 0.33;
//     const purlinT2 = 0.66;
//     const purlinThickness = beamThickness * 0.9;

//     [purlinT1, purlinT2].forEach(t => {
//         beams.push(<AngledBeam key={`purlin_f_${t}`} start={new THREE.Vector3().lerpVectors(points.base_fl, points.top_fl, t)} end={new THREE.Vector3().lerpVectors(points.base_fr, points.top_fr, t)} thickness={purlinThickness} />);
//         beams.push(<AngledBeam key={`purlin_b_${t}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.top_bl, t)} end={new THREE.Vector3().lerpVectors(points.base_br, points.top_br, t)} thickness={purlinThickness} />);
//         beams.push(<AngledBeam key={`purlin_l_${t}`} start={new THREE.Vector3().lerpVectors(points.base_bl, points.top_bl, t)} end={new THREE.Vector3().lerpVectors(points.base_fl, points.top_fl, t)} thickness={purlinThickness} />);
//         beams.push(<AngledBeam key={`purlin_r_${t}`} start={new THREE.Vector3().lerpVectors(points.base_br, points.top_br, t)} end={new THREE.Vector3().lerpVectors(points.base_fr, points.top_fr, t)} thickness={purlinThickness} />);
//     });

//     return beams;
//   }, [points, beamThickness]);

//   return (
//     <group>
//       {/* --- BEAM STRUCTURE --- */}
//       <AngledBeam start={points.base_fl} end={points.base_fr} thickness={beamThickness} />
//       <AngledBeam start={points.base_bl} end={points.base_br} thickness={beamThickness} />
//       <AngledBeam start={points.base_fl} end={points.base_bl} thickness={beamThickness} />
//       <AngledBeam start={points.base_fr} end={points.base_br} thickness={beamThickness} />
//       <AngledBeam start={points.top_fl} end={points.top_fr} thickness={beamThickness} />
//       <AngledBeam start={points.top_bl} end={points.top_br} thickness={beamThickness} />
//       <AngledBeam start={points.top_fl} end={points.top_bl} thickness={beamThickness} />
//       <AngledBeam start={points.top_fr} end={points.top_br} thickness={beamThickness} />
//       <AngledBeam start={points.base_fr} end={points.top_fr} thickness={beamThickness} />
//       <AngledBeam start={points.base_fl} end={points.top_fl} thickness={beamThickness} />
//       <AngledBeam start={points.base_br} end={points.top_br} thickness={beamThickness} />
//       <AngledBeam start={points.base_bl} end={points.top_bl} thickness={beamThickness} />
//       {additionalBeams}

//       {/* --- GLASS PANELS --- */}
//       <GlassPanel materialProps={translucentMaterial} points={[{ x: -points.base_fl.x, y: points.base_fl.y }, { x: points.base_fr.x, y: points.base_fr.y }, { x: points.top_fr.x, y: points.top_fr.y }, { x: -points.top_fl.x, y: points.top_fl.y }]} position-z={points.base_fr.z} rotation-y={Math.PI} />
//       <GlassPanel materialProps={translucentMaterial} points={[{ x: -points.base_bl.x, y: points.base_bl.y }, { x: points.base_br.x, y: points.base_br.y }, { x: points.top_br.x, y: points.top_br.y }, { x: -points.top_bl.x, y: points.top_bl.y }]} position-z={points.base_br.z} />
//       <GlassPanel materialProps={translucentMaterial} points={[{ x: -points.base_br.z, y: points.base_br.y }, { x: points.base_fr.z, y: points.base_fr.y }, { x: points.top_fr.z, y: points.top_fr.y }, { x: -points.top_br.z, y: points.top_br.y }]} position-x={points.base_br.x} rotation-y={-Math.PI / 2} />
//       <GlassPanel materialProps={translucentMaterial} points={[{ x: -points.base_bl.z, y: points.base_bl.y }, { x: points.base_fl.z, y: points.base_fl.y }, { x: points.top_fl.z, y: points.top_fl.y }, { x: -points.top_bl.z, y: points.top_bl.y }]} position-x={points.base_bl.x} rotation-y={Math.PI / 2} />
//       <GlassPanel materialProps={transparentMaterial} points={[{ x: -points.top_fl.x, y: points.top_fl.z }, { x: points.top_fr.x, y: points.top_fr.z }, { x: points.top_br.x, y: -points.top_br.z }, { x: -points.top_bl.x, y: -points.top_bl.z }]} position-y={points.top_fl.y} rotation-x={-Math.PI / 2} />
//     </group>
//   );
// };


// const GalleryWalls = () => {
//   const { walls } = gallerySettings;

//   const [colorMap, roughnessMap, normalMap] = useTexture([
//     walls.colorMap,
//     walls.roughnessMap,
//     walls.normalMap,
//   ]);

//   const wallMaterial = useMemo(() => {
//     [colorMap, roughnessMap, normalMap].forEach(tex => {
//       tex.wrapS = tex.wrapT = RepeatWrapping;
//       tex.repeat.set(...walls.repeat);
//     });
//     return (
//       <meshStandardMaterial
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//     );
//   }, [colorMap, roughnessMap, normalMap, walls]);

//   return (
//     <group>
//       {/* === Walls (All 4 are now solid boxes) === */}
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, -walls.depth / 2]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, walls.depth / 2]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[walls.width / 2, walls.height / 2, 0]}>
//         {wallMaterial}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[-walls.width / 2, walls.height / 2, 0]}>
//         {wallMaterial}
//       </Box>
      
//       {/* === New Roof Lantern Structure === */}
//       <RoofLantern walls={walls} />
//     </group>
//   );
// };





// You might need these placeholders if they aren't defined elsewhere
// const PlaceholderCornice = (props) => <mesh {...props}><boxGeometry args={[props.length, 0.1, 0.1]} /><meshBasicMaterial color="gray" visible={false} /></mesh>;
// const LightStrip = (props) => <mesh {...props}><boxGeometry args={[props.length, 0.02, 0.02]} /><meshBasicMaterial color="white" emissive="white" emissiveIntensity={2} visible={false}/></mesh>;


// const GalleryWalls = () => {
//   const { walls, ceiling } = gallerySettings;

//   const [ceilingColorMap, ceilingRoughnessMap, ceilingNormalMap] = useTexture([
//     ceiling.ceilingColorMap,
//     ceiling.roughnessMap,
//     ceiling.normalMap
//   ]);
//   [ceilingColorMap, ceilingRoughnessMap, ceilingNormalMap].forEach(tex => {
//     tex.wrapS = tex.wrapT = RepeatWrapping;
//     tex.repeat.set(...ceiling.repeat);
//   });

//   const [colorMap, roughnessMap, normalMap] = useTexture([
//     walls.colorMap,
//     walls.roughnessMap,
//     walls.normalMap
//   ]);
//   [colorMap, roughnessMap, normalMap].forEach(tex => {
//     tex.wrapS = tex.wrapT = RepeatWrapping;
//     tex.repeat.set(...walls.repeat);
//   });

//   return (
//     <group>
//       {/* North Wall */}
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, -walls.depth / 2]}>
//         <meshStandardMaterial map={colorMap} roughnessMap={roughnessMap} normalMap={normalMap} roughness={walls.roughness} metalness={walls.metalness} />
//       </Box>
      
//       {/* South Wall */}
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height / 2, walls.depth / 2]}>
//         <meshStandardMaterial map={colorMap} roughnessMap={roughnessMap} normalMap={normalMap} roughness={walls.roughness} metalness={walls.metalness} />
//       </Box>
     
//       {/* East Wall */}
//       <Box args={[0.5, walls.height, walls.depth]} position={[walls.width / 2, walls.height / 2, 0]}>
//         <meshStandardMaterial map={colorMap} roughnessMap={roughnessMap} normalMap={normalMap} roughness={walls.roughness} metalness={walls.metalness} />
//       </Box>
     
//       {/* West Wall */}
//       <Box args={[0.5, walls.height, walls.depth]} position={[-walls.width / 2, walls.height / 2, 0]}>
//         <meshStandardMaterial map={colorMap} roughnessMap={roughnessMap} normalMap={normalMap} roughness={walls.roughness} metalness={walls.metalness} />
//       </Box>
      
//       {/* Ceiling */}
//       <Plane args={[walls.width, walls.depth]} rotation={[Math.PI / 2, 0, 0]} position={[0, walls.height, 0]}>
//         <meshStandardMaterial color="#ffffff" roughness={0.7} metalness={0.1} />
//       </Plane>
//     </group>
//   );
// };




// const GalleryWalls = () => {
//   const { walls, ceiling } = gallerySettings;

//   const [ceilingColorMap, ceilingRoughnessMap, ceilingNormalMap] = useTexture([
//     ceiling.ceilingColorMap,
//     ceiling.roughnessMap,
//     ceiling.normalMap
//   ]);
  
//   [ceilingColorMap, ceilingRoughnessMap, ceilingNormalMap].forEach(tex => {
//     tex.wrapS = tex.wrapT = RepeatWrapping;
//     tex.repeat.set(...ceiling.repeat);
//   });

//   const [colorMap, roughnessMap, normalMap] = useTexture([
//     walls.colorMap,
//     walls.roughnessMap,
//     walls.normalMap
//   ]);
  
//   [colorMap, roughnessMap, normalMap].forEach(tex => {
//     tex.wrapS = tex.wrapT = RepeatWrapping;
//     tex.repeat.set(...walls.repeat);
//   });


//   return (
//     <group>
//       {/* Walls */}
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height/2, -walls.depth/2]}>
//        <meshStandardMaterial 
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//         {/* <meshStandardMaterial color={walls.color} roughness={0.7} metalness={0.1} /> */}
//       </Box>
//       <Box args={[walls.width, walls.height, 0.5]} position={[0, walls.height/2, walls.depth/2]}>
//       <meshStandardMaterial 
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//         {/* <meshStandardMaterial color={walls.color} roughness={0.7} metalness={0.1} /> */}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[-walls.width/2, walls.height/2, 0]}>
//       <meshStandardMaterial 
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//         {/* <meshStandardMaterial color={walls.color} roughness={0.7} metalness={0.1} /> */}
//       </Box>
//       <Box args={[0.5, walls.height, walls.depth]} position={[walls.width/2, walls.height/2, 0]}>
//       <meshStandardMaterial 
//         map={colorMap}
//         roughnessMap={roughnessMap}
//         normalMap={normalMap}
//         roughness={walls.roughness}
//         metalness={walls.metalness}
//       />
//         {/* <meshStandardMaterial color={walls.color} roughness={0.7} metalness={0.1} /> */}
//       </Box>
      
//       {/* Ceiling */}
//       <Plane 
//         args={[walls.width, walls.depth]} 
//         rotation={[Math.PI / 2, 0, 0]} 
//         position={[0, walls.height, 0]}
//       >
//        {/* <meshStandardMaterial 
//         map={ceilingColorMap}
//         roughnessMap={ceilingRoughnessMap}
//         normalMap={ceilingNormalMap}
//         roughness={ceiling.roughness}
//         metalness={ceiling.metalness}
//       /> */}
//       <meshStandardMaterial color="#ffffff" roughness={0.7} metalness={0.1} />
//       </Plane>
//     </group>
//   );
// };

const GalleryPillars = () => {
  // const { pillars } = gallerySettings;

  // const [colorMap, roughnessMap, normalMap, metalnessMap] = useTexture([
  //   pillars.pillarColorMap,
  //   pillars.roughnessMap,
  //   pillars.normalMap,
  //   pillars.metalnessMap
  // ]);

  // // Set wrapping and repeat
  // [colorMap, roughnessMap, normalMap, metalnessMap].forEach(tex => {
  //   tex.wrapS = tex.wrapT = RepeatWrapping;
  //   tex.repeat.set(...pillars.repeat);
  // });

  // const positions = [
  //   [-8, pillars.height/2, -8],
  //   [8, pillars.height/2, -8],
  //   [-8, pillars.height/2, 8],
  //   [8, pillars.height/2, 8]
  // ];
  
  // return (
  //   <group>
  //     {positions.map((pos, index) => (
  //       <Cylinder 
  //         key={index}
  //         args={[pillars.radius, pillars.radius, pillars.height, pillars.segments]}
  //         position={pos}
  //         castShadow
  //         receiveShadow
  //       >
  //         {/* <meshStandardMaterial 
  //           color={pillars.color} 
  //           roughness={0.2} 
  //           metalness={0.1}
  //         /> */}
  //         <meshStandardMaterial
  //           map={colorMap}
  //           roughnessMap={roughnessMap}
  //           normalMap={normalMap}
  //           metalnessMap={metalnessMap}
  //           roughness={pillars.roughness}
  //           metalness={pillars.metalness}
  //         />
  //       </Cylinder>
  //     ))}
  //   </group>
  // );
};


// --- Hologram Shader ---
const HologramMaterial = shaderMaterial(
  { time: 0, color: new THREE.Color(0.1, 0.5, 1.0) },
  `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  `uniform float time; uniform vec3 color; varying vec2 vUv;
   float random(float n) { return fract(sin(n) * 43758.5453123); }
   void main() {
     float scanline = sin(vUv.y * 300.0 - time * 5.0) * 0.1 + 0.9;
     float glitch = random(floor(vUv.y * 20.0 + time)) * 0.1;
     float intensity = scanline - glitch;
     gl_FragColor = vec4(color * intensity, intensity * 0.5);
   }`
);
extend({ HologramMaterial });




// --- NEW EVE-INSPIRED ROBOT CENTERPIECE ---

const LevitationParticles = () => {
    const count = 150;
    const particlesRef = useRef();
    const positions = useMemo(() => {
        const pos = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const r = 0.8 + Math.random() * 0.4;
            pos[i * 3] = Math.cos(angle) * r;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 0.2; // Vertical spread
            pos[i * 3 + 2] = Math.sin(angle) * r;
        }
        return pos;
    }, [count]);

    useFrame((state) => {
        if(particlesRef.current) {
            particlesRef.current.rotation.y = state.clock.getElapsedTime() * 0.3;
        }
    });

    return (
        <points ref={particlesRef}>
            <bufferGeometry attach="geometry">
                <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial size={0.04} color="#00b4d8" transparent opacity={0.7} blending={THREE.AdditiveBlending} depthWrite={false} />
        </points>
    );
};

const Robot = () => {
    const { camera } = useThree();
    const robotGroupRef = useRef();
    const headRef = useRef();
    const textRef = useRef();
    const [greeting, setGreeting] = useState("Welcome!");

    useEffect(() => {
        const timer = setTimeout(() => setGreeting(""), 5000);
        return () => clearTimeout(timer);
    }, []);

    useFrame((state, delta) => {
        const time = state.clock.getElapsedTime();
        // Levitation effect - raised base position to avoid clipping
        if (robotGroupRef.current) {
            robotGroupRef.current.position.y = 2.2 + Math.sin(time * 1.5) * 0.1;
        }
        // Head tracking
        if (headRef.current) {
            const targetPosition = new THREE.Vector3(camera.position.x, headRef.current.position.y, camera.position.z);
            const targetQuaternion = new THREE.Quaternion();
            const tempMatrix = new THREE.Matrix4();
            // Use the robot group's world position for accurate lookAt
            const headWorldPos = new THREE.Vector3();
            headRef.current.getWorldPosition(headWorldPos);
            tempMatrix.lookAt(camera.position, headWorldPos, headRef.current.up);
            targetQuaternion.setFromRotationMatrix(tempMatrix);
            headRef.current.quaternion.slerp(targetQuaternion, delta * 2.0);
        }
        // Greeting fade
        if (textRef.current) {
            textRef.current.material.opacity = THREE.MathUtils.lerp(textRef.current.material.opacity, greeting ? 1 : 0, delta * 5);
        }
    });

    return (
        <group ref={robotGroupRef} scale={1.5}>
            {/* EVE's Body */}
            <Sphere args={[0.5, 32, 32]} scale={[0.8, 1.2, 0.8]} castShadow>
                <meshStandardMaterial color="#e9ecef" roughness={0.1} metalness={0.9} />
            </Sphere>
            {/* EVE's Head */}
            <group ref={headRef} position={[0, 0.6, 0]}>
                <Sphere args={[0.4, 32, 32]} scale={[1, 0.9, 1]} castShadow>
                    <meshStandardMaterial color="#e9ecef" roughness={0.1} metalness={0.9} />
                </Sphere>
                <Sphere args={[0.3, 32, 32]} position={[0, 0, 0.2]}>
                    <meshStandardMaterial color="#000000" roughness={0.0} metalness={1.0} />
                </Sphere>
                {/* Eyes - moved forward to be visible */}
                <Sphere args={[0.05, 16, 16]} position={[-0.1, 0.05, 0.39]}>
                     <meshStandardMaterial color="#00b4d8" emissive="#00b4d8" emissiveIntensity={3} toneMapped={false} />
                </Sphere>
                <Sphere args={[0.05, 16, 16]} position={[0.1, 0.05, 0.39]}>
                     <meshStandardMaterial color="#00b4d8" emissive="#00b4d8" emissiveIntensity={3} toneMapped={false} />
                </Sphere>
            </group>
            {/* EVE's Hands - changed to Capsules */}
            <Capsule args={[0.1, 0.2, 16, 16]} position={[-0.7, 0.1, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
                 <meshStandardMaterial color="#e9ecef" roughness={0.1} metalness={0.9} />
            </Capsule>
             <Capsule args={[0.1, 0.2, 16, 16]} position={[0.7, 0.1, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
                 <meshStandardMaterial color="#e9ecef" roughness={0.1} metalness={0.9} />
            </Capsule>
            {/* Speech Bubble */}
            <Text ref={textRef} position={[0, 1.8, 0]} fontSize={0.2} color="white" anchorX="center">
                {greeting}
                <meshBasicMaterial transparent depthWrite={false} />
            </Text>
            <LevitationParticles />
        </group>
    );
};


// // const CenterAttraction = () => {
//   const pedestalSize = 3.0;
//   return (
//     <group position={[0, 0, 0]}>
//       <Box args={[pedestalSize, 1.5, pedestalSize]} position={[0, 0.75, 0]} receiveShadow>
//         <meshStandardMaterial color="#3d3d3d" roughness={0.5} metalness={0.5} />
//       </Box>
//       {/* Title plates on all four sides */}
//       <PedestalTitlePlate position={[0, 0.6, pedestalSize / 2]} rotation={[0, 0, 0]} />
//       <PedestalTitlePlate position={[0, 0.6, -pedestalSize / 2]} rotation={[0, Math.PI, 0]} />
//       <PedestalTitlePlate position={[pedestalSize / 2, 0.6, 0]} rotation={[0, Math.PI / 2, 0]} />
//       <PedestalTitlePlate position={[-pedestalSize / 2, 0.6, 0]} rotation={[0, -Math.PI / 2, 0]} />
//       <Robot />
//     </group>
//   );
// };


// --- NEW ENCHANTED TREE CENTERPIECE ---

const TreeModel = () => {
    // IMPORTANT: Replace this path with the actual path to your tree model in the /public folder.
    const { scene } = useGLTF('/models/golden_horse_web.glb'); // meshopt-compressed (0.48 MB instead of 1.98 MB)

    useLayoutEffect(() => {
        // This centers the model's geometry at its base, ensuring correct placement.
        const box = new THREE.Box3().setFromObject(scene);
        const center = new THREE.Vector3();
        box.getCenter(center);
        scene.position.x -= box.min.x+1.5;
        scene.position.y -= box.min.y; // Move the model up so its base is at y=0

        scene.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true;
                child.receiveShadow = true;
            }
        });
    }, [scene]);

    return <primitive object={scene} scale={0.06} />;
};

const Fireflies = () => {
    const count = 100;
    const pointsRef = useRef();
    const materialRef = useRef();

    const [positions, velocities, startTimes] = useMemo(() => {
        const pos = new Float32Array(count * 3);
        const vel = new Float32Array(count * 3);
        const times = new Float32Array(count);
        const radius = 0.6; // Reduced radius to keep them inside the case
        const height = 2.6;
        for (let i = 0; i < count; i++) {
            pos[i * 3] = (Math.random() - 0.5) * radius * 2;
            pos[i * 3 + 1] = Math.random() * height;
            pos[i * 3 + 2] = (Math.random() - 0.5) * radius * 2;
            vel[i * 3] = (Math.random() - 0.5) * 0.1;
            vel[i * 3 + 1] = (Math.random() - 0.5) * 0.1;
            vel[i * 3 + 2] = (Math.random() - 0.5) * 0.1;
            times[i] = Math.random() * 10;
        }
        return [pos, vel, times];
    }, [count]);

    useFrame((state, delta) => {
        if (pointsRef.current && materialRef.current) {
            const pos = pointsRef.current.geometry.attributes.position.array;
            const time = state.clock.getElapsedTime();

            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                pos[i3] += velocities[i3] * delta;
                pos[i3 + 1] += velocities[i3 + 1] * delta;
                pos[i3 + 2] += velocities[i3 + 2] * delta;

                // Improved boundary check
                if (Math.abs(pos[i3]) > 1.4) velocities[i3] *= -1;
                if (pos[i3 + 1] < 0.1 || pos[i3 + 1] > 3.8) velocities[i3 + 1] *= -1;
                if (Math.abs(pos[i3 + 2]) > 1.4) velocities[i3 + 2] *= -1;
            }
            pointsRef.current.geometry.attributes.position.needsUpdate = true;
            
            // Blinking effect by animating the material's size
            materialRef.current.size = Math.sin(time) * 0.04 + 0.04;
        }
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
            </bufferGeometry>
            <pointsMaterial ref={materialRef} color="#ffd700" transparent opacity={0.8} blending={THREE.AdditiveBlending} depthWrite={false} />
        </points>
    );
};

// const Rock = (props) => {
//     return (
//         <Icosahedron args={[1, 1]} {...props}>
//             <meshStandardMaterial color="#6c757d" roughness={0.8} flatShading />
//         </Icosahedron>
//     );
// };

// const Grass = () => {
//     const count = 200;
//     const meshRef = useRef();
//     const dummy = useMemo(() => new THREE.Object3D(), []);

//     const positions = useMemo(() => {
//         const pos = [];
//         for (let i = 0; i < count; i++) {
//             pos.push({
//                 position: new THREE.Vector3((Math.random() - 0.5) * 2.8, 0, (Math.random() - 0.5) * 2.8),
//                 scale: 0.5 + Math.random() * 0.5,
//                 rotation: Math.random() * Math.PI * 2,
//             });
//         }
//         return pos;
//     }, [count]);

//     useEffect(() => {
//         if (meshRef.current) {
//             positions.forEach((p, i) => {
//                 dummy.position.copy(p.position);
//                 dummy.rotation.y = p.rotation;
//                 dummy.scale.set(p.scale, p.scale, p.scale);
//                 dummy.updateMatrix();
//                 meshRef.current.setMatrixAt(i, dummy.matrix);
//             });
//             meshRef.current.instanceMatrix.needsUpdate = true;
//         }
//     }, [positions, dummy]);

//     return (
//         <instancedMesh ref={meshRef} args={[null, null, count]}>
//             <planeGeometry args={[0.1, 0.5]} />
//             <meshStandardMaterial color="#556b2f" side={THREE.DoubleSide} />
//         </instancedMesh>
//     );
// };

// const Landscape = () => {
//     return (
//         <group>
//             <Cylinder args={[1.4, 1.4, 0.2, 64]}>
//                 <meshStandardMaterial color="#4a4a4a" />
//             </Cylinder>
//             <Grass />
//             <Rock position={[-0.8, 0.2, 0.5]} scale={0.4} />
//             <Rock position={[1.0, 0.1, -0.7]} scale={0.3} />
//             <Rock position={[0.2, 0.15, 1.1]} scale={0.35} />
//         </group>
//     );
// };

// const PedestalTitlePlate = ({ rotation, position }) => {
//     const plateWidth = 1.8;
//     const plateHeight = 0.3;
//     const plateDepth = 0.05;
//     const fontPath = '/fonts/Bliamer.otf';

//     return (
//         <group position={position} rotation={rotation}>
//             <Box args={[plateWidth, plateHeight, plateDepth]}>
//                 <meshStandardMaterial color="#b59410" roughness={0.4} metalness={0.9} />
//             </Box>
//             <Text position={[0, 0, plateDepth / 2 + 0.01]} fontSize={0.2} color="#2c3e50" font={fontPath}>
//                 David's Artwork
//             </Text>
//         </group>
//     );
// };

const PedestalTitlePlate = ({ rotation, position }) => {
    const plateWidth = 2.0;
    const plateHeight = 0.6;
    const plateDepth = 0.05;
    const fontPath = '/fonts/Bliamer.otf';

    // A bright, warm color for the text
    const textColor = "#FFFFE0"; 
    // A darker, less-saturated bronze for the plaque
    const plaqueColor = "#4a3c2a"; 

    return (
        <group position={position} rotation={rotation}>
            {/* Plaque: Darker for better contrast, still metallic */}
            <Box args={[plateWidth, plateHeight, plateDepth]}>
                <meshStandardMaterial 
                    color={plaqueColor} 
                    roughness={0.2} // A bit shinier
                    metalness={0.8} 
                />
            </Box>
            
            {/* Text: Now bright and emissive */}
            <Text 
                position={[0, 0, plateDepth / 2 + 0.01]} 
                fontSize={0.35} 
                font={fontPath}
                color={textColor}
                // --- Add these properties ---
                emissive={textColor}       // Make it glow this color
                emissiveIntensity={2}    // Adjust this to make it "pop" more
                // -----------------------------
            >
                David's Artwork
            </Text>
        </group>
    );
};

// Secret: click the golden horse five times and it rears up, prances and neighs.
const PrancingHorse = ({ children }) => {
  const ref = useRef();
  const clicks = useRef([]);
  const start = useRef(-1);
  const onClick = (e) => {
    e.stopPropagation();
    const now = performance.now();
    clicks.current = [...clicks.current.filter((t) => now - t < 3000), now];
    if (clicks.current.length >= 5 && start.current < 0) {
      clicks.current = [];
      start.current = now;
      playNeigh();
      announceEgg('horse', 'The golden horse rears up and neighs!', '🐎');
    }
  };
  useFrame(() => {
    const g = ref.current;
    if (!g || start.current < 0) return;
    const t = (performance.now() - start.current) / 1000;
    if (t > 2.1) { g.position.y = 0; g.rotation.set(0, 0, 0); start.current = -1; return; }
    const env = Math.sin(Math.min(1, t / 2.1) * Math.PI);
    g.position.y = Math.abs(Math.sin(t * 6)) * 0.25 * env;           // little hops
    g.rotation.x = Math.sin(t * 3) * 0.12 * env;                         // rocks back on its hind legs
    g.rotation.z = Math.sin(t * 3) * 0.12 * env;
  });
  return (
    <group ref={ref} onClick={onClick} onPointerOver={() => { document.body.style.cursor = 'pointer'; }} onPointerOut={() => { document.body.style.cursor = ''; }}>
      {children}
    </group>
  );
};

const CenterAttraction = () => {
  const pedestalSize = 3.0;
  const pedestalHeight = 1.5;
  const caseHeight = 4.0;
  const lightTarget = useMemo(() => new THREE.Object3D(), []);

  return (
    <group position={[0, 0, 0]}>
      <primitive object={lightTarget} position={[0, pedestalHeight + 3, 0]} />
      <Box args={[pedestalSize, pedestalHeight, pedestalSize]} position={[0, pedestalHeight / 2, 0]} receiveShadow castShadow>
        <meshStandardMaterial color="#1c1c1c" roughness={0.5} metalness={0.1} />
      </Box>
      <PedestalTitlePlate position={[0, pedestalHeight - 0.5, pedestalSize / 2]} rotation={[0, 0, 0]} />
      <PedestalTitlePlate position={[0, pedestalHeight - 0.5, -pedestalSize / 2]} rotation={[0, Math.PI, 0]} />
      <PedestalTitlePlate position={[pedestalSize / 2, pedestalHeight - 0.5, 0]} rotation={[0, Math.PI / 2, 0]} />
      <PedestalTitlePlate position={[-pedestalSize / 2, pedestalHeight - 0.5, 0]} rotation={[0, -Math.PI / 2, 0]} />
      
      <Box args={[pedestalSize, caseHeight, pedestalSize]} position={[0, pedestalHeight + caseHeight / 2, 0]}>
        <meshPhysicalMaterial roughness={0.03} transmission={1} thickness={0.1} ior={1.95} color="#ffffff" transparent />
      </Box>
      
      <group position={[0, pedestalHeight, 0]}>
        {/* <Landscape /> */}
        {/* <Fireflies /> */}
        <PrancingHorse>
          <Suspense fallback={null}>
              <TreeModel />
          </Suspense>
        </PrancingHorse>
        {/* <Fireflies /> */}
      </group>
      
      <spotLight 
        position={[0, pedestalHeight + 0.1, 0]}
        target={lightTarget}
        angle={1.0}
        penumbra={0.8}
        intensity={8}
        color="#FFD700"
        distance={10}
        decay={2}
      />
    </group>
  );
};



// --- REFINED CAMERA CONTROLLER ---



// const CenterAttraction = () => {
//   const meshRef = useRef();

//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y = state.clock.elapsedTime * 0.1;
//     }
//   });

//   return (
//     <group position={[0, 0, 0]}>
//       {/* Base pedestal */}
//       <Cylinder args={[1.5, 1.5, 0.3, 32]} position={[0, 0.15, 0]} receiveShadow>
//         <meshStandardMaterial color="#bfa980" roughness={0.2} metalness={0.9} />
//       </Cylinder>

//       {/* Rotating sculpture */}
//       <group ref={meshRef} position={[0, 1.1, 0]}>
//         {/* Bust stand */}
//         <Cylinder args={[0.3, 0.3, 0.6, 16]} position={[0, 0.3, 0]} castShadow>
//           <meshStandardMaterial color="#ffffff" roughness={0.4} metalness={0.1} />
//         </Cylinder>

//         {/* Decorative top sphere (simulating a marble bust head) */}
//         <Sphere args={[0.4, 32, 32]} position={[0, 0.9, 0]} castShadow>
//           <meshStandardMaterial color="#f5f5f5" roughness={0.5} metalness={0.05} />
//         </Sphere>
//       </group>

//       {/* Gold ring base detail */}
//       <Torus args={[1.1, 0.05, 16, 100]} rotation={[Math.PI / 2, 0, 0]} position={[0, 0.31, 0]}>
//         <meshStandardMaterial color="#FFD700" roughness={0.1} metalness={1} />
//       </Torus>
//     </group>
//   );
// };


// // Center attraction - simple elegant sculpture
// const CenterAttraction = () => {
//   const meshRef = useRef();
  
//   useFrame((state) => {
//     if (meshRef.current) {
//       meshRef.current.rotation.y = state.clock.elapsedTime * 0.1;
//     }
//   });
  
//   return (
//     <group position={[0, 0, 0]}>
//       {/* Base platform */}
//       <Cylinder args={[1.5, 1.5, 0.2, 16]} position={[0, 0.1, 0]} receiveShadow>
//         <meshStandardMaterial color="#d4d4d4" roughness={0.1} metalness={0.3} />
//       </Cylinder>
      
//       {/* Rotating sculpture */}
//       <group ref={meshRef} position={[0, 1, 0]}>
//         {/* Main sculpture body */}
//         <Cylinder args={[0.15, 0.15, 1.5, 8]} position={[0, 0.75, 0]} castShadow>
//           <meshStandardMaterial color="#c0c0c0" roughness={0.2} metalness={0.8} />
//         </Cylinder>
        
//         {/* Decorative spheres */}
//         <Sphere args={[0.3]} position={[0, 1.5, 0]} castShadow>
//           <meshStandardMaterial color="#FFD700" roughness={0.1} metalness={0.9} />
//         </Sphere>
        
//         <Sphere args={[0.2]} position={[0.5, 1, 0]} castShadow>
//           <meshStandardMaterial color="#C0C0C0" roughness={0.1} metalness={0.9} />
//         </Sphere>
        
//         <Sphere args={[0.2]} position={[-0.5, 1, 0]} castShadow>
//           <meshStandardMaterial color="#C0C0C0" roughness={0.1} metalness={0.9} />
//         </Sphere>
//       </group>
      
//       {/* Information plaque */}
//       <Box args={[0.8, 0.3, 0.05]} position={[0, 0.3, 1.7]} castShadow>
//         <meshStandardMaterial color="#2c2c2c" roughness={0.7} metalness={0.1} />
//       </Box>
//     </group>
//   );
// };

// const GalleryLighting = () => {
//   return (
//     <group>
//       <hemisphereLight
//         skyColor="#ffffff"
//         groundColor="#999999"
//         intensity={3.0}
//       />
//       <ambientLight intensity={1.0} color="#ffffff" />
//       <pointLight position={[0, 5, 0]} intensity={20} color="#ffffff" />

//       {/* Optional: one directional light */}
//       <directionalLight
//         position={[5, 10, 5]}
//         intensity={1}
//         color="#ffffff"
//         castShadow
//         shadow-mapSize-width={1024}
//         shadow-mapSize-height={1024}
//       />
//     </group>
//   );
// };

// const GalleryLighting = () => {
//   return (
//     <group>
//       {/* Brighter ambient light */}
//       <ambientLight intensity={1} color="#ffffff" />
      
//       {/* Main directional lights */}
//       <directionalLight 
//         position={[12, 15, 10]} 
//         intensity={1.8} 
//         color="#ccefffff"
//         false
//         shadow-mapSize={[2048, 2048]}
//         shadow-camera-far={50}
//         shadow-camera-left={-15}
//         shadow-camera-right={15}
//         shadow-camera-top={15}
//         shadow-camera-bottom={-15}
//       />
      
//       <directionalLight 
//         position={[-12, 15, 10]} 
//         intensity={2} 
//         color="#ccefffff"
//         castShadow
//       />
      
//       <directionalLight 
//         position={[-12, 15, -10]} 
//         intensity={2} 
//         color="#ccefffff"
//         castShadow
//       />
      
//       {/* Ceiling spotlights */}
//       {/* <spotLight
//         position={[0, 4.5, 0]}
//         angle={0.5}
//         penumbra={0.3}
//         intensity={1.2}
//         color="#fff8dc"
//         castShadow
//       />
      
//       <spotLight
//         position={[6, 4.5, 0]}
//         angle={0.4}
//         penumbra={0.3}
//         intensity={1.0}
//         color="#fff8dc"
//         castShadow
//       />
      
//       <spotLight
//         position={[-6, 4.5, 0]}
//         angle={0.4}
//         penumbra={0.3}
//         intensity={1.0}
//         color="#fff8dc"
//         castShadow
//       /> */}
      
//       {/* Point lights for even coverage */}
//       {/* <pointLight position={[0, 3, 0]} intensity={0.8} color="#fff8dc" />
//       <pointLight position={[5, 3, 5]} intensity={0.6} color="#fff8dc" />
//       <pointLight position={[-5, 3, 5]} intensity={0.6} color="#fff8dc" />
//       <pointLight position={[5, 3, -5]} intensity={0.6} color="#fff8dc" />
//       <pointLight position={[-5, 3, -5]} intensity={0.6} color="#fff8dc" /> */}
//     </group>
//   );
// };


// --- Lens Flare Component ---
// This component creates a procedural lens flare effect that tracks the sun.
const Lensflare = ({ sunPosition }) => {
  const { camera } = useThree();
  const flareRef = useRef();

  // We'll create a few textures for the different flare elements (ghosts)
  const textures = useMemo(() => {
    const loader = new THREE.TextureLoader();
    // A simple circular gradient for the main glow and ghosts
    const flareTexture = new THREE.CanvasTexture(createGradientTexture());
    // You could load other textures for more complex flare shapes
    return [flareTexture];
  }, []);

  useFrame(() => {
    if (!flareRef.current) return;

    const sunVector = new THREE.Vector3().copy(sunPosition);
    const cameraVector = new THREE.Vector3().copy(camera.position);

    // Project the sun's position onto the screen
    const screenPos = sunVector.clone().project(camera);

    // Only show the flare if the sun is roughly in front of the camera
    if (screenPos.z > 1.0) {
      flareRef.current.visible = false;
      return;
    }
    flareRef.current.visible = true;

    // Position the flare elements along the line from the sun to the screen center
    const flareDistance = cameraVector.distanceTo(sunVector);
    const flareDir = new THREE.Vector3().subVectors(sunVector, cameraVector).normalize();

    flareRef.current.children.forEach((child, i) => {
      const t = i / (flareRef.current.children.length - 1);
      child.position.copy(cameraVector).add(flareDir.clone().multiplyScalar(flareDistance * t));
      child.scale.setScalar(0.2 * (1.0 - t));
    });
  });

  // Helper to create a radial gradient texture
  function createGradientTexture() {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 128;
    canvas.height = 128;
    const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 128, 128);
    return canvas;
  }

  return (
    <group ref={flareRef} visible={false}>
      {[...Array(5)].map((_, i) => (
        <Plane key={i} args={[1, 1]} lookAt={camera.position}>
          <meshBasicMaterial
            map={textures[0]}
            transparent
            opacity={0.3}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </Plane>
      ))}
    </group>
  );
};


// --- New Realistic Gallery Lighting Component ---
// Day / night colours for the lights (blended by nightRef, 0 = day, 1 = night).
const LIGHT_DAY = { hemiSky: new THREE.Color('#bde0fe'), hemiGround: new THREE.Color('#c2b280'), hemi: 0.6, sun: new THREE.Color('#fff5e1'), sunI: 2.5, fill: 0.3 };
const LIGHT_NIGHT = { hemiSky: new THREE.Color('#1d2b53'), hemiGround: new THREE.Color('#0b0f1a'), hemi: 0.45, sun: new THREE.Color('#9fb7ff'), sunI: 0.7, fill: 0.12 };

const DUSK_HEMI = new THREE.Color('#ffd2b0');
const _duskSun = new THREE.Color();
const GalleryLighting = ({ nightRef, skyRef, weatherRef, theatreRef }) => {
  const sunRef = useRef();
  const lightRef = useRef();
  const hemiRef = useRef();
  const fillRef = useRef();
  const flareRef = useRef();

  // We use the useFrame hook to animate the sun's position over time.
  useFrame((state) => {
    if (sunRef.current) {
      // This creates a slow, circular rotation for the sun
      const time = state.clock.getElapsedTime();
      sunRef.current.rotation.y = time * 0.02;
    }
    const n = nightRef ? nightRef.current : 0;
    const w = weatherRef ? weatherRef.current : { cloud: 0, flash: 0 };
    const cinema = 1 - 0.85 * (theatreRef ? theatreRef.current : 0); // theatre mode dims the hall
    const sunDim = (1 - w.cloud * 0.5 - (w.storm || 0) * 0.15) * cinema; // clouds block the sun / moon
    const dusk = skyRef ? skyRef.current.dusk : 0;
    if (dusk > 0) duskColor(skyRef.current, _duskSun);
    if (hemiRef.current) {
      hemiRef.current.color.copy(LIGHT_DAY.hemiSky);
      if (dusk > 0) hemiRef.current.color.lerp(DUSK_HEMI, dusk * 0.6); // warm sky light at sunrise / sunset
      hemiRef.current.color.lerp(LIGHT_NIGHT.hemiSky, n);
      hemiRef.current.groundColor.copy(LIGHT_DAY.hemiGround).lerp(LIGHT_NIGHT.hemiGround, n);
      hemiRef.current.intensity = THREE.MathUtils.lerp(LIGHT_DAY.hemi, LIGHT_NIGHT.hemi, n) * (1 - 0.25 * dusk) * (1 - w.cloud * 0.4) * cinema + w.flash * 3;
    }
    if (lightRef.current) {
      // Low, golden sun in the morning and evening (moving a light needs no shader recompile).
      lightRef.current.color.copy(LIGHT_DAY.sun);
      if (dusk > 0) lightRef.current.color.lerp(_duskSun, dusk * 0.85);
      lightRef.current.color.lerp(LIGHT_NIGHT.sun, n);
      lightRef.current.intensity = THREE.MathUtils.lerp(LIGHT_DAY.sunI, LIGHT_NIGHT.sunI, n) * (1 - 0.4 * dusk) * sunDim;
      lightRef.current.position.set(20 + 8 * dusk, 25 - 15 * dusk, 15);
    }
    if (fillRef.current) fillRef.current.intensity = THREE.MathUtils.lerp(LIGHT_DAY.fill, LIGHT_NIGHT.fill, n) * cinema;
    if (flareRef.current) flareRef.current.visible = n < 0.5 && w.cloud < 0.4;
  });

  // Get the world position of the light to pass to the lens flare
  const sunPosition = new THREE.Vector3();
  if (lightRef.current) {
    lightRef.current.getWorldPosition(sunPosition);
  }

  return (
    <group>
      {/* 1. Hemisphere Light: Provides soft, ambient environmental light. */}
      <hemisphereLight 
        ref={hemiRef}
        skyColor={new THREE.Color("#bde0fe")}
        groundColor={new THREE.Color("#c2b280")}
        intensity={0.6} 
      />
      
      {/* This group will rotate, carrying the sun with it. */}
      <group ref={sunRef}>
        {/* 2. Main Sunlight (Directional Light): The primary, shadow-casting light source. */}
        <directionalLight 
          ref={lightRef}
          castShadow
          position={[20, 25, 15]} // Starts high and angled
          intensity={2.5} 
          color="#fff5e1"
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-near={0.5}
          shadow-camera-far={80} // Increased far plane for larger scenes
          shadow-camera-left={-25}
          shadow-camera-right={25}
          shadow-camera-top={25}
          shadow-camera-bottom={-25}
        />
      </group>
      
      {/* 3. Fill Light: A gentle light to soften shadows. */}
      <directionalLight 
        ref={fillRef}
        position={[-10, 10, -10]} 
        intensity={0.3} 
        color="#ffffff"
      />

      {/* 4. Lens Flare Effect: Tracks the main sunlight. */}
      <group ref={flareRef}>
        <Lensflare sunPosition={sunPosition} />
      </group>
    </group>
  );
};


const framePaintingHorizontalPadding = 1.3;
const framePaintingVerticalPadding = 1.6;

// --- Hover Glow Effect ---
// This new component creates a soft glow using a canvas-based radial gradient.
const HoverGlow = React.memo(({ painting }) => {
  const texture = useMemo(() => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = 256;
    canvas.height = 256;
    const gradient = context.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(255, 223, 100, 0.6)');
    gradient.addColorStop(1, 'rgba(255, 223, 100, 0)');
    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(canvas);
  }, []);

  const glowSize = Math.max(painting.size.width, painting.size.height) * 1.5;

  return (
    <Plane args={[glowSize, glowSize]} position={[0, 0, 0.05]}>
      <meshBasicMaterial
        map={texture}
        transparent
        opacity={0.8}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </Plane>
  );
});

// --- Floating Orbs Effect ---
// This new component creates three glowing orbs that orbit the painting.
const FloatingOrbs = React.memo(({ painting }) => {
  const orbs = useRef([]);
  const numOrbs = 3;
  const radius = Math.max(painting.size.width, painting.size.height) * 0.7;

  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    orbs.current.forEach((orb, i) => {
      if (orb) {
        const angle = time * 0.5 + (i / numOrbs) * Math.PI * 2;
        orb.position.x = Math.cos(angle) * radius;
        orb.position.y = Math.sin(angle) * radius;
        orb.position.z = 0.1 + Math.sin(time * 2 + i) * 0.1;
      }
    });
  });

  return (
    <group>
      {[...Array(numOrbs)].map((_, i) => (
        <Sphere key={i} ref={(el) => (orbs.current[i] = el)} args={[0.05, 16, 16]}>
          <meshStandardMaterial
            color="#ffd700"
            emissive="#ffd700"
            emissiveIntensity={3}
            toneMapped={false}
          />
        </Sphere>
      ))}
    </group>
  );
});


// --- Shimmering Aura Effect Definition ---
// This shader material creates the procedural shimmering effect.
const ShimmerMaterial = shaderMaterial(
  // Uniforms
  { time: 0, color: new THREE.Color(1.0, 0.87, 0.4), uOpacity: 0.0 },
  // Vertex Shader
  `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  // Fragment Shader
  `
    uniform float time;
    uniform vec3 color;
    uniform float uOpacity;
    varying vec2 vUv;

    // 2D Random function
    float random (vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
    }

    // 2D Noise function
    float noise (vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        vec2 u = f*f*(3.0-2.0*f);
        return mix(random(i), random(i + vec2(1.0, 0.0)), u.x) +
               (random(i + vec2(0.0, 1.0)) - random(i)) * u.y * (1.0 - u.x) +
               (random(i + vec2(1.0, 1.0)) - random(i + vec2(1.0, 0.0))) * u.x * u.y;
    }

    void main() {
      // Calculate distance from the center for each axis to create a rectangular shape
      vec2 borderDist = abs(vUv - vec2(0.5));
      float rectDist = max(borderDist.x, borderDist.y);
      
      // Generate a procedural noise pattern that changes over time
      float shimmer = noise(vUv * 8.0 + time * 0.8);
      
      // Create a soft border mask using the rectangular distance
      float border = smoothstep(0.35, 0.5, rectDist) * (1.0 - smoothstep(0.5, 0.51, rectDist));
      
      // Combine the border and shimmer to get the final alpha, and multiply by the opacity uniform
      float alpha = border * shimmer * uOpacity;
      
      gl_FragColor = vec4(color, alpha);
    }
  `
);

// Calling extend() at the top-level of the module ensures that react-three-fiber
// is aware of the custom <shimmerMaterial> element before any components try to use it.
extend({ ShimmerMaterial });


// Calling extend() at the top-level of the module ensures that react-three-fiber
// is aware of the custom <shimmerMaterial> element before any components try to use it.
extend({ ShimmerMaterial });


const ShimmeringAura = React.memo(({ painting, isActive }) => {
  const materialRef = useRef();

  // Update the shader's uniforms on each frame
  useFrame((state, delta) => {
    if (materialRef.current) {
      materialRef.current.time = state.clock.getElapsedTime();
      // Animate the opacity for a smooth fade-in and fade-out effect
      materialRef.current.uOpacity = THREE.MathUtils.lerp(
        materialRef.current.uOpacity,
        isActive ? 0.7 : 0, // Target a visible opacity when active
        delta * 10 // Animation speed
      );
    }
  });

  // Calculate the size of the aura to match the painting's aspect ratio
  const auraWidth = painting.size.width * 1.4;
  const auraHeight = painting.size.height * 1.4;

  return (
    <Plane args={[auraWidth, auraHeight]} position={[0, 0, 0.05]}>
      <shimmerMaterial
        ref={materialRef}
        transparent
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </Plane>
  );
});

// Frame model with 1024 px WebP textures (0.46 MB instead of 15.7 MB; same shape).
const FRAME_MODEL_URL = '/models/ornateFrame_web.glb';
useGLTF.preload(FRAME_MODEL_URL);

// --- 3D Frame Model Component ---
const FrameModel = ({ painting }) => {
  const { scene } = useGLTF(FRAME_MODEL_URL);
  const ref = useRef();
  const [scale, setScale] = useState([1, 1, 1]);

  useLayoutEffect(() => {
    const cloned = scene.clone();
    const box = new THREE.Box3().setFromObject(cloned);
    const size = new THREE.Vector3();
    box.getSize(size);

    const targetWidth = painting.size.width + framePaintingHorizontalPadding;
    const targetHeight = painting.size.height + framePaintingVerticalPadding;

    const sx = targetWidth / size.x;
    const sy = targetHeight / size.y;
    const sz = 0.5 / size.z;

    setScale([sx, sy, sz]);
  }, [scene, painting]);

  return (
    <primitive 
      ref={ref}
      object={scene.clone()} 
      scale={scale}
      position={[0, 0, -0.1]}
      castShadow
      receiveShadow
    />
  );
};


// // --- NEW 3D YOUTUBE PLAY BUTTON COMPONENT ---
// // We define this component here to use it in the TitlePlate.

const YouTubePlayButton = ({ onClick,isSelected = false, ...props }) => {
  const materialRef = useRef(null);
  const timeRef = useRef(0);

  // Create the triangle shape for the "play" icon
  const triShape = useMemo(() => {
    const s = new THREE.Shape();
    s.moveTo(-0.04, 0.05);  // Top-left of triangle
    s.lineTo(-0.04, -0.05); // Bottom-left of triangle
    s.lineTo(0.06, 0);     // Right-middle point
    s.closePath();
    return new THREE.ShapeGeometry(s);
  }, []);

  // Animate the shimmer effect
  // useFrame(() => {
  //   if (materialRef.current) {
  //     timeRef.current += 0.04;
  //     // Create pulsing emissive intensity
  //     const shimmer = Math.sin(timeRef.current) * 0.3 + 0.1; // Ranges from 0.1 to 0.7
  //     materialRef.current.emissiveIntensity = shimmer;
  //   }
  // });

    useFrame(() => {
    if (materialRef.current) {
      if (isSelected) {  // ← Only shimmer when selected
        timeRef.current += 0.04;
        const shimmer = Math.sin(timeRef.current) * 0.3 + 0.2;
        materialRef.current.emissiveIntensity = shimmer;
      } else {
        materialRef.current.emissiveIntensity = 0;
      }
    }
  });

  const handleClick = (e) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <group {...props} onClick={handleClick}>
      {/* Red rounded rectangle base with shimmer */}
      <Box args={[0.3, 0.22, 0.01]} position={[0, 0, -0.005]}>
        <meshStandardMaterial 
          ref={materialRef}
          color="#FF0000" 
          roughness={0.3} 
          metalness={0.9}
          emissive="#FF3333" // Light red glow
          emissiveIntensity={0.4}
        />
      </Box>

      {/* White triangle icon */}
      <mesh geometry={triShape} position={[0.01, 0, 0.01]}>
        <meshBasicMaterial color="#FFFFFF" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};



// --- Title Plate Component (NEW and IMPROVED) ---
const TitlePlate = ({ painting, isSelected = false, ...props }) => {
  const plateWidth = painting.size.width;
  const { gl } = useThree(); // <-- ADD THIS LINE
  const plateHeight = 0.6;
  const plateDepth = 0.07;
  const yPosition = -painting.size.height / 2 - (framePaintingVerticalPadding / 2) - (plateHeight / 2) - 0.05;

  const screwRadius = 0.01;
  const screwPosition = plateWidth / 2 - 0.02;
  const screwHeightPosition = plateHeight / 2 - 0.02;

  const fontPath = '/fonts/Bliamer.otf';

  const hasVideo = !!painting.youtubeUrl;

  // Dynamically change layout based on video presence
  // If video: Text is on the left, button on the right.
  // If no video: Text is centered.
  const textMaxWidth = hasVideo ? plateWidth * 0.9 - 0.4 : plateWidth * 0.9;
  const textAnchorX = hasVideo ? 'left' : 'center';
  const textPositionX = hasVideo ? -plateWidth / 2 + 0.1 : 0;

  // Click handler for the new button
  const handleYouTubeClick = () => {
    window.open(painting.youtubeUrl, '_blank');
  };

  return (
    <group 
      position={[0, yPosition, 0.05]}
      onPointerOver={(e) => { 
        e.stopPropagation(); 
        gl.domElement.style.cursor = 'pointer'; 
      }}
      onPointerOut={(e) => {
        // DO NOT stop propagation
        // DO NOT set the cursor to 'grab'
      }}
    >
      {/* Metal Plate Mesh */}
      <Box args={[plateWidth, plateHeight, plateDepth]}>
        <meshStandardMaterial color="#b59410" roughness={0.4} metalness={2.5} />
      </Box>

      {/* Screws at the 4 corners */}
      <Cylinder args={[screwRadius, screwRadius, plateDepth + 0.01, 16]} position={[-screwPosition, screwHeightPosition, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#6c757d" roughness={0.3} metalness={1.0} />
      </Cylinder>
      <Cylinder args={[screwRadius, screwRadius, plateDepth + 0.01, 16]} position={[screwPosition, screwHeightPosition, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#6c757d" roughness={0.3} metalness={1.0} />
      </Cylinder>
      <Cylinder args={[screwRadius, screwRadius, plateDepth + 0.01, 16]} position={[-screwPosition, -screwHeightPosition, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#6c757d" roughness={0.3} metalness={1.0} />
      </Cylinder>
      <Cylinder args={[screwRadius, screwRadius, plateDepth + 0.01, 16]} position={[screwPosition, -screwHeightPosition, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <meshStandardMaterial color="#6c757d" roughness={0.3} metalness={1.0} />
      </Cylinder>

      {/* Embossed Text Shadow */}
      <Text
        position={[0.001, -0.001, plateDepth / 2 + 0.01]}
        fontSize={0.2}
        color="#50C878"
        anchorX="center"
        anchorY="middle"
        maxWidth={plateWidth * 0.9}
        font={fontPath}
      >
        {painting.title}
      </Text>
      {/* Main Embossed Text */}
      <Text
        position={[0, 0, plateDepth / 2 + 0.012]}
        fontSize={0.2}
        anchorX="center"
        anchorY="middle"
        maxWidth={plateWidth * 0.9}
        font={fontPath}
      >
        {painting.title}
        {/* By nesting a material, we override the default and can add emissive properties */}
        <meshStandardMaterial
          color="#1a0949ff"
          emissive="#4e9a3dff"
          emissiveIntensity={0.5}
          toneMapped={false}
        />
      </Text>

      {/* --- NEW YOUTUBE BUTTON --- */}
      {/* Conditionally render the button if a URL exists */}
      {hasVideo && (
        <YouTubePlayButton
          onClick={handleYouTubeClick}
          isSelected={isSelected}  // ← Pass this prop
          // Position it on the far right of the plate
          position={[plateWidth / 2 - 0.25, 0, plateDepth / 2 + 0.01]}
        />
      )}

    </group>
  );
};




// --- Main Artwork Frame Component ---
export const ArtworkFrame = ({ painting, onClick, onDoubleClick, onHover, onHoverOut, isSelected }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  const { gl } = useThree(); // <-- ADD THIS LINE
  const [texture, setTexture] = useState();
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickedPaintingId, setLastClickedPaintingId] = useState(null);
  
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.crossOrigin = 'anonymous';
    loader.load(
      paintingImage(painting, 'texture'), // optimised copy, not the multi-MB original
      (loadedTexture) => {
        loadedTexture.colorSpace = THREE.SRGBColorSpace;
        loadedTexture.anisotropy = 4; // sharper when viewed at an angle
        setTexture(loadedTexture);
        setLoading(false);
      },
      undefined,
      (error) => {
        console.error('Error loading texture for:', painting.title, error);
        setLoading(false);
      }
    );
  }, [painting]);


  // --- THE FIX IS HERE ---
  // This effect ensures that the hover state is turned off when a painting is selected.
  useEffect(() => {
    if (isSelected) {
      setHovered(false);
    }
  }, [isSelected]);


  useFrame((state) => {
    if (meshRef.current && isSelected) {
      meshRef.current.rotation.y = painting.rotation.y + Math.sin(state.clock.elapsedTime * 0.8) * 0.005;
    }
    // Secret (Konami code): the paintings sway to a silent tune for a few seconds.
    if (meshRef.current) {
      const left = eggState.konamiUntil - performance.now();
      const amt = left > 0 ? Math.min(1, left / 1500, (9000 - left) / 800) : 0;
      meshRef.current.rotation.z = painting.rotation.z + Math.sin(state.clock.elapsedTime * 4 + painting.id * 1.7) * 0.07 * amt;
    }
  });
  
  // const handleClick = (e) => {
  //   e.stopPropagation();
  //   if (isSelected) {
  //     if (painting.youtubeUrl) setShowVideo(true);
  //   } else {
  //     onClick(painting);
  //   }
  // };

  const handleClick = (e) => {
    e.stopPropagation();

    const now = Date.now();
    const timeDifference = now - lastClickTime;
    const isSamePainting = lastClickedPaintingId === painting.id;

    // ✅ DOUBLE-CLICK: 2 clicks within 300ms on SAME selected painting
    if (timeDifference < 300 && isSamePainting && isSelected) {
      if (onDoubleClick) {
        onDoubleClick(painting);  // Opens ImageViewer
      }
      setLastClickTime(0);
      setLastClickedPaintingId(null);
    } 
    // ✅ SINGLE-CLICK: First click or different painting
    else {
      if (onClick) {
        onClick(painting);  // Focus camera
      }
      setLastClickTime(now);
      setLastClickedPaintingId(painting.id);
    }
  };

  const handleCloseVideo = () => {
    setShowVideo(false);
  };
  
  const handlePointerOver = (e) => {
    e.stopPropagation();
    if (isSelected) return; // <-- FIX: Disable hover if selected
    setHovered(true);
    onHover(painting);
    gl.domElement.style.cursor = 'pointer';
  };
  
  const handlePointerOut = (e) => {
    //e.stopPropagation();
    if (isSelected) return; // <-- FIX: Disable hover if selected
    setHovered(false);
    onHoverOut();
    // gl.domElement.style.cursor = 'grab';
  };
  
  return (
    <group 
      ref={meshRef}
      position={[
        painting.position.x,
        painting.centerY ?? (gallerySettings.walls.height / 2), // Vertically center on the wall
        painting.position.z
      ]}
      rotation={[painting.rotation.x, painting.rotation.y, painting.rotation.z]}
    >
      {/* 3D Ornate Frame */}
      <FrameModel painting={painting} />

      {/* White background mat board */}
      <Plane
        args={[painting.size.width + 0.15, painting.size.height + 0.15]}
        position={[0, 0, -0.03]}
        receiveShadow
      >
        <meshStandardMaterial color="#f0f0f0" side={THREE.DoubleSide} />
      </Plane>

      {/* Artwork Plane */}
      <Plane 
        args={[painting.size.width, painting.size.height]}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        castShadow
        receiveShadow
      >
        {loading ? (
          <meshBasicMaterial color="#cccccc" side={THREE.DoubleSide} />
        ) : texture ? (
          <meshStandardMaterial 
            map={texture} 
            roughness={0.2} 
            metalness={0.1}
            emissive={(hovered && !isSelected) ? "#444444" : "#000000"} // <-- FIX: Conditional emissive
            emissiveIntensity={0.5}
            toneMapped={false}
            side={THREE.DoubleSide}
          />
        ) : (
          <meshBasicMaterial color="red" side={THREE.DoubleSide} />
        )}
      </Plane>

      {/* FIX: Conditionally render the glow only when hovered and not selected */}
      {/* {(hovered && !isSelected) && <HoverGlow painting={painting} />} */}
      {/* {(hovered && !isSelected) && <FloatingOrbs painting={painting} />} */}
      {/* {(hovered && !isSelected) && <ShimmeringAura painting={painting} />} */}
        {/* The shimmering aura is now always rendered, but its visibility is controlled by the 'isActive' prop */}
      <ShimmeringAura painting={painting} isActive={hovered && !isSelected} />


      {/* --- NEW TITLE PLATE --- */}
      <TitlePlate 
        painting={painting} 
        isSelected={isSelected} 
      />

      YouTube Video Frame
      {/* {showVideo && (
        <YouTubeFrame
          youtubeUrl={painting.youtubeUrl}
          isVisible={showVideo}
          painting={painting}
          onClose={handleCloseVideo}
        />
      )} */}
      
      {/* Video Play Indicator */}
      {/* {isSelected && !showVideo && painting.youtubeUrl && (
        <group position={[painting.size.width/2 - 0.3, painting.size.height/2 - 0.3, 0.01]}>
          <Plane args={[0.4, 0.4]}>
            <meshBasicMaterial color="#FF0000" transparent opacity={0.8} side={THREE.DoubleSide} />
          </Plane>
          <Plane args={[0.2, 0.2]} position={[0, 0, 0.01]}>
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.9} side={THREE.DoubleSide} />
          </Plane>
        </group>
      )} */}
    </group>
  );
};



// --- REFINED CAMERA CONTROLLER ---

export const CameraController = ({ 
  currentPainting, 
  focusOnPainting, 
  onMovementComplete,
  onUnselectPainting,
  floorClickPosition,
  moveToCenter,
  // manualMovement, // OLD: Prop is no longer used.
  uiMovement, // FIX 1: New prop to receive movement commands from UI buttons.
  theatreMode = false,
  onTheatreSeated,
  bounds = gallerySettings.boundaries, // walkable area (another room passes its own)
  paintings = mockPaintings,           // used to stop walking into paintings
  walkTo = null,                       // { position: [x,y,z], lookAt: [x,y,z] } – scripted walk (e.g. to a door)
  joystickRef = null,                  // mobile joysticks: { mx, my } to walk, { lx, ly } to look (-1..1)
  onWalkDone,
}) => {
  const { camera, gl } = useThree();
  const controlsRef = useRef();
  // Theatre: 'idle' (normal gallery) | 'entering' | 'seated' | 'leaving'. While not idle, the
  // visitor's movement, dragging and zoom are ignored and this controller drives the camera.
  const theatre = useRef({ phase: 'idle', t: 0, fromPos: new THREE.Vector3(), fromTarget: new THREE.Vector3(),
    savedPos: new THREE.Vector3(), savedTarget: new THREE.Vector3(), look: new THREE.Vector3() });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const isDragging = useRef(false);
  // First-person look: turn (yaw) and look up/down (pitch) around where you stand. Walking always
  // stays on the floor. phi is the angle of the view from straight up: 0.2 ≈ looking almost
  // straight up at the glass roof, PI/2 level, PI/2 + 1.1 ≈ 63° down.
  const lookVel = useRef({ x: 0, y: 0 }); // eased look-stick turning speed (rad/s)
  const LOOK_PHI_MIN = 0.2, LOOK_PHI_MAX = Math.PI / 2 + 1.1;
  const applyLook = (dYaw, dPitch) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const offset = controls.target.clone().sub(camera.position);
    const sph = new THREE.Spherical().setFromVector3(offset);
    sph.theta -= dYaw;
    sph.phi = Math.min(LOOK_PHI_MAX, Math.max(LOOK_PHI_MIN, sph.phi + dPitch));
    offset.setFromSpherical(sph);
    controls.target.copy(camera.position).add(offset);
    controls.update();
  };
  // Start with a level view (the default look-at point is on the floor in the middle of the room).
  useEffect(() => {
    const c = controlsRef.current;
    if (c && Math.abs(c.target.y - camera.position.y) > 0.01) { c.target.y = camera.position.y; c.update(); }
  }, [camera]);

  // Horizontal "forward" and "right" from the view direction (ignores looking up or down).
  const flatDirections = () => {
    const controls = controlsRef.current;
    const fwd = controls ? controls.target.clone().sub(camera.position) : new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    fwd.y = 0;
    if (fwd.lengthSq() < 1e-8) fwd.set(0, 0, -1);
    fwd.normalize();
    const right = new THREE.Vector3(-fwd.z, 0, fwd.x);
    return { fwd, right };
  };
  const lastMousePosition = useRef({ x: 0, y: 0 });

  // --- State for Continuous Keyboard Movement ---
  const movement = useRef({ forward: false, backward: false, left: false, right: false });

  // --- Helper functions ---
  const isNearArtworkForward = (position, direction) => {
    const minDistance = 1.5;
    if (direction !== 'forward') return false;
    for (let painting of paintings) {
      const artworkPos = new THREE.Vector3(painting.position.x, 2, painting.position.z);
      if (position.distanceTo(artworkPos) < minDistance) return true;
    }
    return false;
  };
  const enforceBoundaries = (position) => {
    const { minX, maxX, minZ, maxZ } = bounds;
    position.x = Math.max(minX, Math.min(maxX, position.x));
    position.z = Math.max(minZ, Math.min(maxZ, position.z));
    // FIX: Use a consistent eye-level height for the camera
    position.y = 3; 
    return position;
  };

  // --- Keyboard Listeners for Continuous Movement ---
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTypingTarget(e.target)) return; // typing in the guestbook, not walking
      if (theatre.current.phase !== 'idle') return; // seated in the theatre
      if (isTransitioning) setIsTransitioning(false);
      onUnselectPainting?.();
      switch (e.code) {
        case 'ArrowUp': case 'KeyW': movement.current.forward = true; break;
        case 'ArrowDown': case 'KeyS': movement.current.backward = true; break;
        case 'ArrowLeft': case 'KeyA': movement.current.left = true; break;
        case 'ArrowRight': case 'KeyD': movement.current.right = true; break;
      }
    };
    const handleKeyUp = (e) => {
      switch (e.code) {
        case 'ArrowUp': case 'KeyW': movement.current.forward = false; break;
        case 'ArrowDown': case 'KeyS': movement.current.backward = false; break;
        case 'ArrowLeft': case 'KeyA': movement.current.left = false; break;
        case 'ArrowRight': case 'KeyD': movement.current.right = false; break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [onUnselectPainting, isTransitioning]);

  // --- Main Animation Loop for Keyboard Movement ---
  useFrame((state, delta) => {
    if (!controlsRef.current || isTransitioning) return;
    if (theatre.current.phase !== 'idle') return;

    const controls = controlsRef.current;
    const speed = 3.0 * delta;

    const { fwd: forward, right } = flatDirections();

    // mobile joysticks: the right stick looks around, the left stick walks (analog)
    const js = joystickRef ? joystickRef.current : null;
    // Look stick: a gentle curve (small pushes turn slowly for fine aiming, a full push turns at about
    // 70°/s sideways and 40°/s up/down), eased in and out so the view never jerks.
    if (js) {
      const curve = (v) => Math.sign(v) * v * v;
      const ease = Math.min(1, delta * 10);
      lookVel.current.x += (curve(js.lx || 0) * 1.2 - lookVel.current.x) * ease;
      lookVel.current.y += (curve(js.ly || 0) * 0.7 - lookVel.current.y) * ease;
      if (Math.abs(lookVel.current.x) > 1e-4 || Math.abs(lookVel.current.y) > 1e-4) applyLook(lookVel.current.x * delta, lookVel.current.y * delta);
    }

    const moveDirection = new THREE.Vector3();
    if (js && (js.mx || js.my)) moveDirection.addScaledVector(forward, -js.my).addScaledVector(right, js.mx);
    // FIX 1: The useFrame loop now checks for input from BOTH the keyboard (movement.current)
    // AND the on-screen UI buttons (uiMovement prop).
    if (movement.current.forward || (uiMovement && uiMovement.forward)) moveDirection.add(forward);
    if (movement.current.backward || (uiMovement && uiMovement.backward)) moveDirection.sub(forward);
    if (movement.current.left || (uiMovement && uiMovement.left)) moveDirection.sub(right);
    if (movement.current.right || (uiMovement && uiMovement.right)) moveDirection.add(right);

    // if (movement.current.forward) moveDirection.add(forward);
    // if (movement.current.backward) moveDirection.sub(forward);
    // if (movement.current.left) moveDirection.sub(right);
    // if (movement.current.right) moveDirection.add(right);

    if (moveDirection.length() > 0) {
      if (moveDirection.length() > 1) moveDirection.normalize();
      let newPosition = camera.position.clone().add(moveDirection.multiplyScalar(speed));
      
      if ((movement.current.forward || (js && js.my < -0.2)) && isNearArtworkForward(newPosition, 'forward')) {
          newPosition = camera.position.clone();
      }
      const boundedPosition = enforceBoundaries(newPosition);
      
      const offset = controls.target.clone().sub(camera.position);
      camera.position.copy(boundedPosition);
      controls.target.copy(camera.position).add(offset);
      controls.update();
    }
  });

  // --- RESTORED: All original click-based animations ---
  useEffect(() => {
    if (focusOnPainting && currentPainting && controlsRef.current) {
      setIsTransitioning(true);
      const offset = currentPainting.cameraOffset;
      if (!offset) { setIsTransitioning(false); return; }
      const paintingPos = new THREE.Vector3(
        currentPainting.position.x + offset.x,
        offset.y, // FIX: Use the y-offset from the painting data for the look-at target
        currentPainting.position.z + offset.z
      );
      const forward = new THREE.Vector3(0, 0, 1).applyEuler(new THREE.Euler(currentPainting.rotation.x, currentPainting.rotation.y, currentPainting.rotation.z));
      const finalDistance = Math.max(Math.max(currentPainting.size.width, currentPainting.size.height) * 1.8, 1.5);
      const targetPosition = paintingPos.clone().add(forward.multiplyScalar(finalDistance));
      
      // Pass the correct height to the boundary function
      const boundedPosition = enforceBoundaries(targetPosition);
      boundedPosition.y = offset.y;
      
      let animationId;
      const animateToTarget = () => {
        if (!isTransitioning) { cancelAnimationFrame(animationId); return; }
        const posDist = camera.position.distanceTo(boundedPosition);
        const lookDist = controlsRef.current.target.distanceTo(paintingPos);
        camera.position.lerp(boundedPosition, 0.06);
        controlsRef.current.target.lerp(paintingPos, 0.06);
        controlsRef.current.update();
        if (posDist > 0.1 || lookDist > 0.1) {
          animationId = requestAnimationFrame(animateToTarget);
        } else {
          setIsTransitioning(false);
          onMovementComplete?.();
        }
      };
      animateToTarget();
      return () => { cancelAnimationFrame(animationId); };
    }
  }, [focusOnPainting, currentPainting, isTransitioning]);
  
  useEffect(() => {
    if (floorClickPosition && controlsRef.current) {
      setIsTransitioning(true);
      onUnselectPainting?.();
      const targetPosition = new THREE.Vector3(floorClickPosition.x, 3, floorClickPosition.z); // Use consistent height
      const boundedPosition = enforceBoundaries(targetPosition);
      const currentOffset = controlsRef.current.target.clone().sub(camera.position);
      
      let animationId;
      const animateToFloor = () => {
        if (!isTransitioning) { cancelAnimationFrame(animationId); return; }
        const distance = camera.position.distanceTo(boundedPosition);
        camera.position.lerp(boundedPosition, 0.08);
        controlsRef.current.target.copy(camera.position).add(currentOffset);
        controlsRef.current.update();
        if (distance > 0.1) {
          animationId = requestAnimationFrame(animateToFloor);
        } else {
          setIsTransitioning(false);
          onMovementComplete?.();
        }
      };
      animateToFloor();
      return () => { cancelAnimationFrame(animationId); };
    }
  }, [floorClickPosition, isTransitioning]);
  
  useEffect(() => {
    if (moveToCenter && controlsRef.current) {
      setIsTransitioning(true);
      onUnselectPainting?.();
      const centerPosition = new THREE.Vector3(0, 3, 0); // Use consistent height
      const lookDirection = new THREE.Vector3(0, 0, -1);
      const targetLookAt = centerPosition.clone().add(lookDirection.multiplyScalar(2));
      
      let animationId;
      const animateToCenter = () => {
        if (!isTransitioning) { cancelAnimationFrame(animationId); return; }
        const posDist = camera.position.distanceTo(centerPosition);
        const lookDist = controlsRef.current.target.distanceTo(targetLookAt);
        camera.position.lerp(centerPosition, 0.06);
        controlsRef.current.target.lerp(targetLookAt, 0.06);
        controlsRef.current.update();
        if (posDist > 0.1 || lookDist > 0.1) {
          animationId = requestAnimationFrame(animateToCenter);
        } else {
          setIsTransitioning(false);
          onMovementComplete?.();
        }
      };
      animateToCenter();
      return () => { cancelAnimationFrame(animationId); };
    }
  }, [moveToCenter, isTransitioning]);

  // --- RESTORED AND REFINED: Mouse Drag Controls ---
  useEffect(() => {
    if (!controlsRef.current) return;
    const controls = controlsRef.current;
    const domElement = gl.domElement;
    
    const handleMouseDown = (event) => {
      if (theatre.current.phase !== 'idle') return;
      isDragging.current = true;
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
      onUnselectPainting?.();
    };
    const handleMouseMove = (event) => {
      if (!isDragging.current) return;
      const deltaX = event.clientX - lastMousePosition.current.x;
      const deltaY = event.clientY - lastMousePosition.current.y;
      // drag left/right to turn, up/down to look up or down (walk with the keys, wheel or arrows)
      applyLook(deltaX * 0.005, deltaY * 0.004);
      lastMousePosition.current = { x: event.clientX, y: event.clientY };
    };
    const handleMouseUp = () => { isDragging.current = false; };
    
// FIX 2: Added touch event handlers to enable drag-to-move on mobile.
    const handleTouchStart = (event) => {
        if (theatre.current.phase !== 'idle') return;
        isDragging.current = true;
        const touch = event.targetTouches[0]; // the finger on the 3D view (not one on a joystick)
        lastMousePosition.current = { x: touch.clientX, y: touch.clientY };
        onUnselectPainting?.();
    };
    const handleTouchMove = (event) => {
        if (!isDragging.current) return;
        const touch = event.targetTouches[0];
        if (!touch) return;
        const deltaX = touch.clientX - lastMousePosition.current.x;
        const deltaY = touch.clientY - lastMousePosition.current.y;
        // swipe to look around, up and down too (walk with the left joystick)
        applyLook(deltaX * 0.006, deltaY * 0.005);
        lastMousePosition.current = { x: touch.clientX, y: touch.clientY };
    };
    const handleTouchEnd = () => { isDragging.current = false; };

    const handleWheel = (event) => {
      if (theatre.current.phase !== 'idle') return;
      const { fwd } = flatDirections();
      const step = Math.max(-0.8, Math.min(0.8, -event.deltaY * 0.004));
      let next = camera.position.clone().addScaledVector(fwd, step);
      if (step > 0 && isNearArtworkForward(next, 'forward')) return;
      next = enforceBoundaries(next);
      const offset = controls.target.clone().sub(camera.position);
      camera.position.copy(next);
      controls.target.copy(camera.position).add(offset);
      controls.update();
    };
    domElement.addEventListener('wheel', handleWheel, { passive: true });
    domElement.addEventListener('mousedown', handleMouseDown);
    domElement.addEventListener('mousemove', handleMouseMove);
    domElement.addEventListener('mouseup', handleMouseUp);
    // FIX 2: Registering the new touch event listeners.
    domElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    domElement.addEventListener('touchmove', handleTouchMove, { passive: true });
    domElement.addEventListener('touchend', handleTouchEnd);
    return () => {
      domElement.removeEventListener('wheel', handleWheel);
      domElement.removeEventListener('mousedown', handleMouseDown);
      domElement.removeEventListener('mousemove', handleMouseMove);
      domElement.removeEventListener('mouseup', handleMouseUp);
      // FIX 2: Cleaning up the new touch event listeners.
      domElement.removeEventListener('touchstart', handleTouchStart);
      domElement.removeEventListener('touchmove', handleTouchMove);
      domElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onUnselectPainting]);

  // Set initial target
  useEffect(() => {
    if (controlsRef.current) {
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      controlsRef.current.target.copy(camera.position).add(forward);
      controlsRef.current.update();
    }
  }, [camera]);

  // --- Theatre mode: walk to the recliner, sit, and later return to where the visitor was ---
  useEffect(() => {
    const th = theatre.current;
    const controls = controlsRef.current;
    if (!controls) return;
    if (theatreMode && (th.phase === 'idle' || th.phase === 'leaving')) {
      if (th.phase === 'idle') {
        th.savedPos.copy(camera.position);
        th.savedTarget.copy(controls.target);
      }
      setIsTransitioning(false);
      movement.current = { forward: false, backward: false, left: false, right: false };
      isDragging.current = false;
      controls.enabled = false; // stops OrbitControls from levelling the camera
      th.fromPos.copy(camera.position);
      th.fromTarget.copy(controls.target);
      th.phase = 'entering';
      th.t = 0;
    } else if (!theatreMode && (th.phase === 'entering' || th.phase === 'seated')) {
      th.fromPos.copy(camera.position);
      th.fromTarget.copy(th.look);
      th.phase = 'leaving';
      th.t = 0;
    }
  }, [theatreMode, camera]);

  // --- Scripted walk (to a door): same idea as theatre, but ends by calling onWalkDone ---
  useEffect(() => {
    const th = theatre.current;
    const controls = controlsRef.current;
    if (!controls) return;
    if (walkTo && th.phase === 'idle') {
      setIsTransitioning(false);
      movement.current = { forward: false, backward: false, left: false, right: false };
      isDragging.current = false;
      controls.enabled = false;
      th.fromPos.copy(camera.position);
      th.fromTarget.copy(controls.target);
      th.walkPos = new THREE.Vector3(...walkTo.position);
      th.walkLook = new THREE.Vector3(...walkTo.lookAt);
      th.phase = 'walking';
      th.t = 0;
    } else if (!walkTo && (th.phase === 'walking' || th.phase === 'arrived')) {
      // Walk cancelled: hand control back to the visitor where they stand.
      controls.target.copy(th.look);
      controls.target.y = camera.position.y;
      controls.enabled = true;
      controls.update();
      th.phase = 'idle';
    }
  }, [walkTo, camera]);

  useFrame((state, delta) => {
    const th = theatre.current;
    const controls = controlsRef.current;
    if (th.phase === 'walking' && controls) {
      th.t = Math.min(1, th.t + delta / 1.8);
      const k = th.t * th.t * (3 - 2 * th.t);
      camera.position.copy(th.fromPos).lerp(th.walkPos, k);
      th.look.copy(th.fromTarget).lerp(th.walkLook, Math.min(1, k * 1.5));
      camera.lookAt(th.look);
      if (th.t >= 1) { th.phase = 'arrived'; onWalkDone?.(); }
      return;
    }
    if (th.phase === 'idle' || th.phase === 'seated' || th.phase === 'arrived' || !controls) return;
    const duration = th.phase === 'entering' ? 3.2 : 2.2;
    th.t = Math.min(1, th.t + delta / duration);
    const k = th.t * th.t * (3 - 2 * th.t);
    if (th.phase === 'entering') {
      // Walk to just behind the recliner at standing height, then lower into the seat.
      const stand = new THREE.Vector3(THEATRE.seatEye.x, 3, THEATRE.seatEye.z + 1.6);
      const a = th.fromPos.clone().lerp(stand, k);
      const b = stand.clone().lerp(THEATRE.seatEye, k);
      camera.position.copy(a.lerp(b, k));
      th.look.copy(th.fromTarget).lerp(THEATRE.lookAt, Math.min(1, k * 1.4));
    } else {
      camera.position.copy(th.fromPos).lerp(th.savedPos, k);
      th.look.copy(th.fromTarget).lerp(th.savedTarget, k);
    }
    camera.lookAt(th.look);
    if (th.t >= 1) {
      if (th.phase === 'entering') {
        th.phase = 'seated';
        onTheatreSeated?.(true);
      } else {
        th.phase = 'idle';
        controls.target.copy(th.savedTarget);
        controls.enabled = true;
        controls.update();
      }
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      args={[camera, gl.domElement]}
      enablePan={false}
      enableZoom={false}   // the wheel walks instead (see handleWheel)
      enableRotate={false} // looking around is handled manually (applyLook)
      minPolarAngle={Math.PI - LOOK_PHI_MAX}
      maxPolarAngle={Math.PI - LOOK_PHI_MIN}
      enableDamping={true}
      dampingFactor={0.05}
      screenSpacePanning={false}
    />
  );
};


// const CameraController = ({ 
//   currentPainting, 
//   isMobile, 
//   manualMovement, 
//   onMovementComplete,
//   focusOnPainting,
//   isGuideMode,
//   onUnselectPainting,
//   floorClickPosition,
//   moveToCenter
// }) => {
//   const { camera } = useThree();
//   const controlsRef = useRef();
//   const [isTransitioning, setIsTransitioning] = useState(false);
//   const isDragging = useRef(false);
//   const lastMousePosition = useRef({ x: 0, y: 0 });
  
//   // Helper function to check if camera is too close to any artwork (only for forward movement)
//   const isNearArtworkForward = (position, direction) => {
//     const minDistance = 1.5; // Changed to 1.5 units
    
//     // Only check for forward movement restriction
//     if (direction !== 'forward') {
//       return false;
//     }
    
//     for (let painting of mockPaintings) {
//       const artworkPos = new THREE.Vector3(
//         painting.position.x,
//         painting.position.y + 2,
//         painting.position.z
//       );
//       const distance = position.distanceTo(artworkPos);
//       if (distance < minDistance) {
//         return true;
//       }
//     }
//     return false;
//   };
  
//   // Helper function to enforce boundaries
//   const enforceBoundaries = (position) => {
//     const bounds = gallerySettings.boundaries;
//     return new THREE.Vector3(
//       Math.max(bounds.minX, Math.min(bounds.maxX, position.x)),
//       Math.max(bounds.minY, Math.min(bounds.maxY, position.y)),
//       Math.max(bounds.minZ, Math.min(bounds.maxZ, position.z))
//     );
//   };
  
//   // Focus on painting when clicked
//   // useEffect(() => {
//   //   if (focusOnPainting && currentPainting && controlsRef.current) {
//   //     setIsTransitioning(true);
      
//   //     const paintingPos = new THREE.Vector3(
//   //       currentPainting.position.x,
//   //       currentPainting.position.y + 2,
//   //       currentPainting.position.z
//   //     );
      
//   //     // Calculate the forward direction of the painting
//   //     const forward = new THREE.Vector3(0, 0, 1);
//   //     forward.applyEuler(new THREE.Euler(
//   //       currentPainting.rotation.x,
//   //       currentPainting.rotation.y,
//   //       currentPainting.rotation.z
//   //     ));
      
//   //     // Position camera in front of the painting at proper viewing distance
//   //     const calculatedDistance = Math.max(currentPainting.size.width, currentPainting.size.height) * 1.8;
//   //     const minDistance = 1.5; // Minimum boundary distance
      
//   //     // Use the larger of calculated distance or minimum distance
//   //     const finalDistance = Math.max(calculatedDistance, minDistance);
//   //     const targetPosition = paintingPos.clone().add(forward.multiplyScalar(finalDistance));
      
//   //     // Enforce gallery boundaries (walls)
//   //     const boundedPosition = enforceBoundaries(targetPosition);
      
//   //     let animationId;
//   //     const animateToTarget = () => {
//   //       const positionDistance = camera.position.distanceTo(boundedPosition);
//   //       const lookAtDistance = controlsRef.current.target.distanceTo(paintingPos);
        
//   //       camera.position.lerp(boundedPosition, 0.06);
//   //       controlsRef.current.target.lerp(paintingPos, 0.06);
//   //       controlsRef.current.update();
        
//   //       if (positionDistance > 0.1 || lookAtDistance > 0.1) {
//   //         animationId = requestAnimationFrame(animateToTarget);
//   //       } else {
//   //         setIsTransitioning(false);
//   //         onMovementComplete?.();
//   //       }
//   //     };
      
//   //     animateToTarget();
      
//   //     return () => {
//   //       if (animationId) {
//   //         cancelAnimationFrame(animationId);
//   //       }
//   //     };
//   //   }
//   // }, [focusOnPainting, currentPainting, camera, onMovementComplete]);
  
// // Focus on painting when clicked

// useEffect(() => {
//     if (focusOnPainting && currentPainting && controlsRef.current) {
//       setIsTransitioning(true);
      
     
//     const offset = currentPainting.cameraOffset;

//     if (!offset) {
//       console.error(`David: Missing cameraOffset for painting "${currentPainting.title}"`);
//       setIsTransitioning(false);
//       return;
//     }

//     const paintingPos = new THREE.Vector3(
//       currentPainting.position.x + offset.x,
//       // currentPainting.position.y + offset.y,
//       currentPainting.position.y + (gallerySettings.walls.height - currentPainting.size.height) / 2 + 0.9,
//       currentPainting.position.z + offset.z
//     );
//       // Calculate the forward direction of the painting
//       const forward = new THREE.Vector3(0, 0, 1);
//       forward.applyEuler(new THREE.Euler(
//         currentPainting.rotation.x,
//         currentPainting.rotation.y,
//         currentPainting.rotation.z
//       ));
      
//       // Position camera in front of the painting at proper viewing distance
//       const calculatedDistance = Math.max(currentPainting.size.width, currentPainting.size.height) * 1.8;
//       const minDistance = 1.5; // Minimum boundary distance
      
//       // Use the larger of calculated distance or minimum distance
//       const finalDistance = Math.max(calculatedDistance, minDistance);
//       const targetPosition = paintingPos.clone().add(forward.multiplyScalar(finalDistance));
      
//       // Enforce gallery boundaries (walls)
//       const boundedPosition = enforceBoundaries(targetPosition);
      
//       let animationId;
//       const animateToTarget = () => {
//         const positionDistance = camera.position.distanceTo(boundedPosition);
//         const lookAtDistance = controlsRef.current.target.distanceTo(paintingPos);
        
//         camera.position.lerp(boundedPosition, 0.06);
//         controlsRef.current.target.lerp(paintingPos, 0.06);
//         controlsRef.current.update();
        
//         if (positionDistance > 0.1 || lookAtDistance > 0.1) {
//           animationId = requestAnimationFrame(animateToTarget);
//         } else {
//           setIsTransitioning(false);
//           onMovementComplete?.();
//         }
//       };
      
//       animateToTarget();
      
//       return () => {
//         if (animationId) {
//           cancelAnimationFrame(animationId);
//         }
//       };
//     }
//   }, [focusOnPainting, currentPainting, camera, onMovementComplete]);

//   // Handle floor click movement
//   useEffect(() => {
//     if (floorClickPosition && controlsRef.current) {
//       setIsTransitioning(true);
//       onUnselectPainting?.();
      
//       // Target position is the clicked floor position at camera height
//       const targetPosition = new THREE.Vector3(
//         floorClickPosition.x,
//         camera.position.y, // Keep current camera height
//         floorClickPosition.z
//       );
      
//       // Enforce boundaries
//       const boundedPosition = enforceBoundaries(targetPosition);
      
//       // Preserve the current look direction by keeping the same relative target offset
//       const currentOffset = controlsRef.current.target.clone().sub(camera.position);
      
//       let animationId;
//       const animateToFloor = () => {
//         const distance = camera.position.distanceTo(boundedPosition);
        
//         // Smoothly move camera position
//         camera.position.lerp(boundedPosition, 0.08);
        
//         // Update target to maintain the same look direction
//         controlsRef.current.target.copy(camera.position).add(currentOffset);
//         controlsRef.current.update();
        
//         if (distance > 0.1) {
//           animationId = requestAnimationFrame(animateToFloor);
//         } else {
//           setIsTransitioning(false);
//           onMovementComplete?.();
//         }
//       };
      
//       animateToFloor();
      
//       return () => {
//         if (animationId) {
//           cancelAnimationFrame(animationId);
//         }
//       };
//     }
//   }, [floorClickPosition, camera, onMovementComplete, onUnselectPainting]);
  
//   // Handle move to center (when switching from guide to free roam)
//   useEffect(() => {
//     if (moveToCenter && controlsRef.current) {
//       setIsTransitioning(true);
//       onUnselectPainting?.();
      
//       // Center position of the hall
//       const centerPosition = new THREE.Vector3(0, 2, 0);
      
//       // Look towards the north wall (back wall)
//       const lookDirection = new THREE.Vector3(0, 0, -1);
//       const targetLookAt = centerPosition.clone().add(lookDirection.multiplyScalar(2));
      
//       let animationId;
//       const animateToCenter = () => {
//         const positionDistance = camera.position.distanceTo(centerPosition);
//         const lookAtDistance = controlsRef.current.target.distanceTo(targetLookAt);
        
//         camera.position.lerp(centerPosition, 0.06);
//         controlsRef.current.target.lerp(targetLookAt, 0.06);
//         controlsRef.current.update();
        
//         if (positionDistance > 0.1 || lookAtDistance > 0.1) {
//           animationId = requestAnimationFrame(animateToCenter);
//         } else {
//           setIsTransitioning(false);
//           onMovementComplete?.();
//         }
//       };
      
//       animateToCenter();
      
//       return () => {
//         if (animationId) {
//           cancelAnimationFrame(animationId);
//         }
//       };
//     }
//   }, [moveToCenter, camera, onMovementComplete, onUnselectPainting]);
  
//   // Handle manual movement (WASD/Arrow keys)
//   useEffect(() => {
//     if (manualMovement && controlsRef.current) {
//       onUnselectPainting?.();
      
//       const controls = controlsRef.current;
//       const camera = controls.object;
//       const moveSpeed = 1.5;
      
//       let newPosition = camera.position.clone();
      
//       // Get camera's local directions
//       const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
//       const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
      
//       // Keep movement on horizontal plane only
//       forward.y = 0;
//       right.y = 0;
//       forward.normalize();
//       right.normalize();
      
//       switch(manualMovement) {
//         case 'forward':
//           newPosition.add(forward.multiplyScalar(moveSpeed));
//           // Check if forward movement would get too close to artwork
//           if (isNearArtworkForward(newPosition, 'forward')) {
//             newPosition = camera.position.clone(); // Don't move if too close
//           }
//           break;
//         case 'backward':
//           newPosition.add(forward.multiplyScalar(-moveSpeed));
//           // Backward movement is not restricted
//           break;
//         case 'left':
//           newPosition.add(right.multiplyScalar(-moveSpeed));
//           // Left movement is not restricted
//           break;
//         case 'right':
//           newPosition.add(right.multiplyScalar(moveSpeed));
//           // Right movement is not restricted
//           break;
//       }
      
//       // Enforce gallery boundaries (but not artwork boundaries for non-forward movement)
//       const boundedPosition = enforceBoundaries(newPosition);
      
//       // Update both camera position and target (so camera rotates around itself)
//       const offset = controls.target.clone().sub(camera.position);
      
//       let animationId;
//       const animateMovement = () => {
//         const distance = camera.position.distanceTo(boundedPosition);
//         camera.position.lerp(boundedPosition, 0.15);
//         controls.target.copy(camera.position).add(offset);
//         controls.update();
        
//         if (distance > 0.05) {
//           animationId = requestAnimationFrame(animateMovement);
//         } else {
//           onMovementComplete?.();
//         }
//       };
      
//       animateMovement();
      
//       return () => {
//         if (animationId) {
//           cancelAnimationFrame(animationId);
//         }
//       };
//     }
//   }, [manualMovement, onMovementComplete, onUnselectPainting]);
  
//   // Custom mouse controls
//   useEffect(() => {
//     if (!controlsRef.current) return;
    
//     const controls = controlsRef.current;
//     const domElement = controls.domElement;
    
//     const handleMouseDown = (event) => {
//       isDragging.current = true;
//       lastMousePosition.current = { x: event.clientX, y: event.clientY };
//       onUnselectPainting?.();
//     };
    
//     const handleMouseMove = (event) => {
//       if (!isDragging.current) return;
      
//       const deltaX = event.clientX - lastMousePosition.current.x;
//       const deltaY = event.clientY - lastMousePosition.current.y;
      
//       const rotateSpeed = 0.005;
//       const moveSpeed = 0.02;
      
//       // Horizontal mouse movement = rotation around Y axis (yaw)
//       if (Math.abs(deltaX) > Math.abs(deltaY)) {
//         // Rotate camera around its current position
//         const spherical = new THREE.Spherical();
//         const offset = controls.target.clone().sub(camera.position);
//         spherical.setFromVector3(offset);
//         spherical.theta -= deltaX * rotateSpeed;
//         offset.setFromSpherical(spherical);
//         controls.target.copy(camera.position).add(offset);
//       } else {
//         // Vertical mouse movement = forward/backward movement
//         const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
//         forward.y = 0;
//         forward.normalize();
        
//         const moveDirection = forward.multiplyScalar(-deltaY * moveSpeed);
//         const newPosition = camera.position.clone().add(moveDirection);
//         const boundedPosition = enforceBoundaries(newPosition);
        
//         const offset = controls.target.clone().sub(camera.position);
//         camera.position.copy(boundedPosition);
//         controls.target.copy(camera.position).add(offset);
//       }
      
//       controls.update();
//       lastMousePosition.current = { x: event.clientX, y: event.clientY };
//     };
    
//     const handleMouseUp = () => {
//       isDragging.current = false;
//     };
    
//     domElement.addEventListener('mousedown', handleMouseDown);
//     domElement.addEventListener('mousemove', handleMouseMove);
//     domElement.addEventListener('mouseup', handleMouseUp);
    
//     return () => {
//       domElement.removeEventListener('mousedown', handleMouseDown);
//       domElement.removeEventListener('mousemove', handleMouseMove);
//       domElement.removeEventListener('mouseup', handleMouseUp);
//     };
//   }, [onUnselectPainting]);
  
//   // Set initial target to be in front of camera
//   useEffect(() => {
//     if (controlsRef.current) {
//       const controls = controlsRef.current;
//       const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
//       controls.target.copy(camera.position).add(forward.multiplyScalar(2));
//       controls.update();
//     }
//   }, [camera]);
  
//   return (
//     <OrbitControls
//       ref={controlsRef}
//       enablePan={false}
//       enableZoom={true}
//       enableRotate={false} // We handle rotation manually
//       minDistance={0.1}
//       maxDistance={0.1} // Keep target close to camera
//       minPolarAngle={Math.PI / 2} // Lock vertical rotation
//       maxPolarAngle={Math.PI / 2} // Lock vertical rotation
//       enableDamping={false} // Disable damping for immediate response
//       zoomSpeed={1.0}
//     />
//   );
// };

export const distributePaintings = (paintings, wallWidth, wallDepth, wallHeight) => {
  const grouped = {
    north: [],
    south: [],
    east: [],
    west: []
  };

  // Group paintings by side
  paintings.forEach(p => grouped[p.side]?.push(p));

  const placed = [];

  for (const [side, group] of Object.entries(grouped)) {
    const count = group.length;
    if (count === 0) continue;

    const gap = 1.5; // min space between paintings
    const totalWidth = group.reduce((sum, p) => sum + p.size.width, 0);
    const spacing = Math.max(((side === 'north' || side === 'south' ? wallWidth : wallDepth) - totalWidth) / (count + 1), gap);

    let currentOffset = -((count - 1) * spacing) / 2;

    group.forEach((painting, index) => {
      const baseY = (wallHeight - painting.size.height) / 2;
      let position = { x: 0, y: baseY, z: 0 };
      let rotation = { x: 0, y: 0, z: 0 };

      switch (side) {
        case 'north':
          position.x = currentOffset + index * spacing;
          position.z = -wallDepth / 2 + 0.25;
          rotation.y = 0;
          break;
        case 'south':
          position.x = currentOffset + index * spacing;
          position.z = wallDepth / 2 - 0.25;
          rotation.y = Math.PI;
          break;
        case 'east':
          position.z = currentOffset + index * spacing;
          position.x = wallWidth / 2 - 0.25;
          rotation.y = -Math.PI / 2;
          break;
        case 'west':
          position.z = currentOffset + index * spacing;
          position.x = -wallWidth / 2 + 0.25;
          rotation.y = Math.PI / 2;
          break;
      }

      painting.position = position;
      painting.rotation = rotation;

      // Optional camera offset based on wall height
      painting.cameraOffset = {
        x: 0,
        y: wallHeight * 0.5,
        z: 4
      };

      placed.push(painting);
    });
  }

  return placed;
};

const Gallery3DScene = ({ 
  currentPaintingIndex, 
  onPaintingClick, 
  onPaintingDoubleClick,
  onPaintingHover, 
  onPaintingHoverOut,
  isMobile,
  // manualMovement,
  onMovementComplete,
  focusOnPainting,
  isGuideMode,
  onFloorClick,
  onUnselectPainting,
  floorClickPosition,
  moveToCenter,
  uiMovement, // FIX 1: Receive the uiMovement prop
  nightRef,
  skyRef,
  timeOfDay,
  localTimeOfDay,
  joystickRef,
  weatherRef,
  theatreRef,
  theatreMode,
  onTheatreSeated,
  theatreVideo,
  salonDoorOpen = false,
  onEnterSalon,
  onWindowClick,
  onWindowHover,
  onWindowHoverOut,
  onDoorHover,
  onDoorHoverOut,
  walkTo,
  onWalkDone,
}) => {
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickedPaintingId, setLastClickedPaintingId] = useState(null);
    
  const currentPainting = mockPaintings[currentPaintingIndex];
  const festival = useMemo(() => festivalToday(), []); // decorations for today's festival, if any
  //   const autoArrangedPaintings = distributePaintings(
  //   mockPaintings,
  //   gallerySettings.walls.width,
  //   gallerySettings.walls.depth,
  //   gallerySettings.walls.height
  // );

  // const currentPainting = autoArrangedPaintings[currentPaintingIndex];

  return (
    <>
      <GalleryLighting nightRef={nightRef} skyRef={skyRef} weatherRef={weatherRef} theatreRef={theatreRef} />
      <Moon nightRef={nightRef} weatherRef={weatherRef} />
      <TwinklingStars nightRef={nightRef} weatherRef={weatherRef} />
      <ShootingStars nightRef={nightRef} weatherRef={weatherRef} meteors={!!festival?.meteors} />
      <Aurora nightRef={nightRef} weatherRef={weatherRef} />
      <Rain weatherRef={weatherRef} />
      <Snow weatherRef={weatherRef} />
      <Leaves weatherRef={weatherRef} nightRef={nightRef} />
      <Landscape weatherRef={weatherRef} />
      <Puddles weatherRef={weatherRef} />
      <EasterEggs weatherRef={weatherRef} nightRef={nightRef} paintings={mockPaintings} wallHeight={gallerySettings.walls.height} />
      <Wildlife weatherRef={weatherRef} nightRef={nightRef} />
      <Festivals festival={festival} nightRef={nightRef} weatherRef={weatherRef} />
      <TheatreSet theatreRef={theatreRef} video={theatreVideo ? <ScreenVideo>{theatreVideo}</ScreenVideo> : null} />
      <PictureLights paintings={mockPaintings} wallHeight={gallerySettings.walls.height} nightRef={nightRef} />
      <GalleryFloor onFloorClick={onFloorClick} isGuideMode={isGuideMode} />
      <GalleryWalls weatherRef={weatherRef} windowHandlers={{ onWindowClick, onWindowHover, onWindowHoverOut }} />
      <GalleryPillars />
      <CenterAttraction />
      
      {mockPaintings.map((painting) => (
        <ArtworkFrame
          key={painting.id}
          painting={painting}
          onClick={onPaintingClick}
          onDoubleClick={onPaintingDoubleClick}  
          onHover={onPaintingHover}
          onHoverOut={onPaintingHoverOut}
          isSelected={painting.id === currentPainting.id}
          isGuideMode={isGuideMode}
        />
      ))}
      
      <CameraController 
        currentPainting={currentPainting} 
        joystickRef={joystickRef}
        isMobile={isMobile}
        // manualMovement={manualMovement}
        onMovementComplete={onMovementComplete}
        focusOnPainting={focusOnPainting}
        isGuideMode={isGuideMode}
        onUnselectPainting={onUnselectPainting}
        floorClickPosition={floorClickPosition}
        moveToCenter={moveToCenter}
        uiMovement={uiMovement} // FIX 1: Pass the prop down to the controller
        theatreMode={theatreMode}
        onTheatreSeated={onTheatreSeated}
        walkTo={walkTo}
        onWalkDone={onWalkDone}
      />

      {/* Wall clock above the glass door, showing the time in the gallery's world */}
      <WallClock
        timeOfDay={timeOfDay}
        localTimeOfDay={localTimeOfDay}
        nightRef={nightRef}
        position={[0, 4.75, gallerySettings.walls.depth / 2 - 0.27]}
        rotation={[0, Math.PI, 0]}
      />

      {/* Glass door to the Grand Salon, in the middle of the south wall */}
      <GlassDoor
        position={[0, 0, gallerySettings.walls.depth / 2]}
        rotation={[0, Math.PI, 0]}
        open={salonDoorOpen}
        onEnter={onEnterSalon}
        onHover={onDoorHover}
        onHoverOut={onDoorHoverOut}
      />
      
      {/* <fog attach="fog" args={[gallerySettings.environment.fogColor, 0, 800]} /> */}
    </>
  );
};

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-amber-900"></div>
  </div>
);

const Gallery3D = ({ 
  currentPaintingIndex, 
  onPaintingClick, 
  onPaintingDoubleClick, 
  onPaintingHover, 
  onPaintingHoverOut,
  // manualMovement,
  onMovementComplete,
  focusOnPainting,
  isGuideMode,
  onFloorClick,
  onUnselectPainting,
  floorClickPosition,
  moveToCenter,
  uiMovement, // FIX 1: Receive the uiMovement prop
  isNight = false,
  timeOfDay = isNight ? 'night' : 'afternoon', // 'morning' | 'afternoon' | 'evening' | 'night'
  localTimeOfDay = timeOfDay,                  // the visitor's own time of day (the wall clock shows real time then)
  joystickRef = null,                          // mobile joysticks (see VirtualJoystick.jsx)
  climate = 'clear',
  weatherRef: sharedWeatherRef, // optional: the app shares it with the weather sounds
  theatreMode = false,
  onTheatreSeated,
  theatreVideo = null, // player element shown on the 3D screen (desktop)
  salonDoorOpen = false,
  onEnterSalon,
  onWindowClick,
  onWindowHover,
  onWindowHoverOut,
  onDoorHover,
  onDoorHoverOut,
  walkTo = null,
  onWalkDone,
  spawnAtDoor = false, // coming back from the Grand Salon: start just inside the door
  onReady,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const nightRef = useRef((TIMES[timeOfDay] || TIMES.afternoon).night);
  const skyRef = useRef(createSkyState(TIMES[timeOfDay] ? timeOfDay : 'afternoon'));
  const ownWeatherRef = useRef(createWeatherState(climate));
  const weatherRef = sharedWeatherRef || ownWeatherRef;
  const theatreRef = useRef(0);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    
    <div className="w-full h-full">
      {/* --- CSS for Custom Cursors --- */}
      {/* Add this style tag to your main App or index.html */}
      <style>{`
        .cursor-wall {
          cursor: url('/icons/open-hand.svg') 16 16, auto;
        }
        .cursor-floor {
          cursor: url('/icons/footsteps.svg') 16 16, auto;
        }
        .cursor-painting {
          cursor: url('/icons/paintbrush.svg') 16 16, auto;
        }
      `}</style>
      <Canvas
        camera={{ position: spawnAtDoor ? [0, 3, 9.5] : [0, 3, 8], fov: isMobile ? 65 : 60 }}
        shadows
        gl={{ 
          antialias: true, 
          alpha: false,
          powerPreference: "high-performance"
        }}
        dpr={isMobile ? [1, 1.5] : [1, 2]} // lower on phones to save GPU memory
        performance={{ min: 0.5 }}
      >
        <AdaptiveDpr pixelated />
        <AdaptiveEvents makeDefault/>
        <Suspense fallback={null}>
          <DayNightDriver timeOfDay={timeOfDay} nightRef={nightRef} skyRef={skyRef} />
          <WeatherDriver climate={climate} weatherRef={weatherRef} nightRef={nightRef} skyRef={skyRef} />
          <TheatreDriver active={theatreMode} theatreRef={theatreRef} />
          <RotatingHDRISkyDome hdriPath="/textures/sunny_day_2k.hdr" nightRef={nightRef} skyRef={skyRef} weatherRef={weatherRef} theatreRef={theatreRef} />

          <Gallery3DScene
            currentPaintingIndex={currentPaintingIndex}
            onPaintingClick={onPaintingClick}
            onPaintingDoubleClick={onPaintingDoubleClick}
            onPaintingHover={onPaintingHover}
            onPaintingHoverOut={onPaintingHoverOut}
            isMobile={isMobile}
            // manualMovement={manualMovement}
            onMovementComplete={onMovementComplete}
            focusOnPainting={focusOnPainting}
            isGuideMode={isGuideMode}
            onFloorClick={onFloorClick}
            onUnselectPainting={onUnselectPainting}
            floorClickPosition={floorClickPosition}
            moveToCenter={moveToCenter}
            uiMovement={uiMovement} // FIX 1: Pass the prop down to the scene
            nightRef={nightRef}
            skyRef={skyRef}
            timeOfDay={timeOfDay}
            localTimeOfDay={localTimeOfDay}
            joystickRef={joystickRef}
            weatherRef={weatherRef}
            theatreRef={theatreRef}
            theatreMode={theatreMode}
            onTheatreSeated={onTheatreSeated}
            theatreVideo={theatreVideo}
            salonDoorOpen={salonDoorOpen}
            onEnterSalon={onEnterSalon}
            onWindowClick={onWindowClick}
            onWindowHover={onWindowHover}
            onWindowHoverOut={onWindowHoverOut}
            onDoorHover={onDoorHover}
            onDoorHoverOut={onDoorHoverOut}
            walkTo={walkTo}
            onWalkDone={onWalkDone}
          />
          <ShaderPrewarm />
          <ReadySignal onReady={onReady} />
        </Suspense>
      </Canvas>
    </div>
  );
};

export default Gallery3D;